<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-1 sm:p-2"
      @click.self="close"
    >
      <div class="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full h-full max-w-[100vw] sm:max-w-[98vw] max-h-[100vh] sm:max-h-[98vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 flex-shrink-0">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <div>
                <h2 class="text-xl font-bold">Freshdesk Lite</h2>
                <p class="text-green-100 text-sm">Prehled ticketu</p>
              </div>
              <!-- Today's stats - clickable (client-side filter) -->
              <div v-if="analysisData?.todayStats" class="flex items-center gap-3 ml-4 pl-4 border-l border-white/30">
                <span class="text-sm text-green-100">Dnes:</span>
                <button
                  @click="toggleHeaderFilter('new_today')"
                  class="flex items-center gap-1 px-2 py-0.5 rounded text-sm transition cursor-pointer"
                  :class="headerFilter === 'new_today' ? 'bg-white text-green-700 font-semibold' : 'bg-white/20 hover:bg-white/30'"
                  title="Filtrovat nove dnes"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                  </svg>
                  {{ analysisData.todayStats.newToday }}
                </button>
                <button
                  @click="toggleHeaderFilter('updated_today')"
                  class="flex items-center gap-1 px-2 py-0.5 rounded text-sm transition cursor-pointer"
                  :class="headerFilter === 'updated_today' ? 'bg-white text-green-700 font-semibold' : 'bg-white/20 hover:bg-white/30'"
                  title="Filtrovat aktualizovane dnes"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ analysisData.todayStats.updatedToday }}
                </button>
                <button
                  @click="toggleHeaderFilter('closed_today')"
                  class="flex items-center gap-1 px-2 py-0.5 rounded text-sm transition cursor-pointer"
                  :class="headerFilter === 'closed_today' ? 'bg-white text-green-700 font-semibold' : 'bg-white/20 hover:bg-white/30'"
                  title="Filtrovat uzavrene dnes"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {{ analysisData.todayStats.closedToday }}
                </button>
              </div>
              <!-- Last updated - in header -->
              <div v-if="lastFetched" class="flex items-center gap-1 ml-4 pl-4 border-l border-white/30 text-sm text-green-100">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ formatDateTime(lastFetched) }}
              </div>
            </div>
            <div class="flex items-center gap-2">
              <!-- Filter button - to reopen filter panel -->
              <button
                v-if="analysisData"
                @click="showFilterPanel = true"
                class="flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Zmenit filtr"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span class="text-sm">Filtr</span>
              </button>
              <!-- Refresh button -->
              <button
                v-if="analysisData"
                @click="fetchTickets"
                :disabled="isLoading"
                class="flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
                title="Obnovit data"
              >
                <svg
                  class="w-4 h-4"
                  :class="{ 'animate-spin': isLoading }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span class="text-sm">Obnovit</span>
              </button>
              <!-- Close button -->
              <button
                @click="close"
                class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div class="bg-white border-b border-gray-200 px-4 flex-shrink-0">
          <nav class="flex justify-between items-center">
            <div class="flex gap-1">
              <button
                @click="activeTab = 'tickets'"
              :class="[
                'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'tickets'
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              <span class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Tickety
                <span v-if="analysisData" class="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {{ analysisData.total }}
                </span>
              </span>
            </button>
            <button
              @click="activeTab = 'tasks'"
              :class="[
                'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'tasks'
                  ? 'border-orange-600 text-orange-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              <span class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Ukoly
                <span v-if="taskStats.pending > 0" class="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                  {{ taskStats.pending }}
                </span>
              </span>
            </button>
            <button
              @click="activeTab = 'technicians'"
              :class="[
                'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'technicians'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              <span class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Technici
              </span>
            </button>
            </div>

            <!-- Stats Button -->
            <button
              @click="showStatsModal = true"
              class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Statistiky
            </button>
          </nav>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Loading (only for tickets tab) -->
          <div
            v-if="isLoading"
            class="flex-1 flex flex-col items-center justify-center p-8"
          >
            <!-- Progress bar container -->
            <div class="w-64 mb-6">
              <div class="flex justify-between text-sm text-gray-600 mb-2">
                <span>{{ loadingStatus }}</span>
                <span v-if="loadingProgress > 0">{{ loadingProgress }}%</span>
              </div>
              <div class="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  class="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-300 ease-out"
                  :style="{ width: `${loadingProgress}%` }"
                ></div>
              </div>
            </div>
            <!-- Thermometer animation -->
            <div class="relative mb-4">
              <svg class="w-16 h-16 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              <div class="absolute inset-0 flex items-center justify-center">
                <span class="text-xs font-bold text-green-700">{{ loadingTicketsCount }}</span>
              </div>
            </div>
            <p class="text-lg font-semibold text-gray-700">{{ loadingTitle }}</p>
            <p class="text-sm text-gray-500 mt-1">{{ loadingDetail }}</p>
          </div>

          <!-- Error -->
          <div
            v-else-if="error"
            class="flex-1 flex flex-col items-center justify-center p-8"
          >
            <div class="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md text-center">
              <svg class="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 class="text-lg font-semibold text-red-800 mb-2">Chyba pri nacitani</h3>
              <p class="text-red-600 mb-4">{{ error }}</p>
              <button
                @click="fetchTickets"
                class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Zkusit znovu
              </button>
            </div>
          </div>

          <!-- Filter Panel - shown before loading or when reopened (only on tickets tab) -->
          <div
            v-else-if="activeTab === 'tickets' && (!analysisData || showFilterPanel)"
            class="flex-1 flex flex-col items-center justify-center p-8"
          >
            <div class="w-full max-w-4xl space-y-4">
            <!-- AI Summary Search - Independent -->
            <div class="bg-white rounded-xl border border-blue-200 shadow-lg p-5">
              <h3 class="text-base font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Hledat v AI souhrnech
                <span class="text-xs font-normal text-blue-600">(jmeno, email, misto, popis)</span>
              </h3>
              <div class="flex gap-2">
                <div class="relative flex-1">
                  <input
                    v-model="summarySearchQuery"
                    type="text"
                    placeholder="Zadej hledany text..."
                    class="w-full border border-blue-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    @keyup.enter="searchInSummaries"
                    @input="hasSearched = false; summarySearchResults = []; searchError = ''"
                  />
                  <div class="absolute left-3 top-1/2 -translate-y-1/2">
                    <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                <button
                  @click="searchInSummaries"
                  :disabled="!summarySearchQuery || summarySearchQuery.length < 2 || isSearchingSummaries"
                  class="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                >
                  <svg v-if="isSearchingSummaries" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span v-else>Hledat</span>
                </button>
              </div>
              <!-- Search results -->
              <div v-if="summarySearchResults.length > 0" class="mt-3">
                <div class="text-sm text-blue-700 mb-2 font-medium">
                  Nalezeno {{ summarySearchResults.length }} ticketu
                </div>
                <div class="max-h-48 overflow-y-auto space-y-1">
                  <div
                    v-for="result in summarySearchResults.slice(0, 10)"
                    :key="result.ticketId"
                    class="text-xs bg-blue-50 p-2 rounded border border-blue-100 cursor-pointer hover:bg-blue-100 transition"
                    @click="loadSearchedTicket(result.ticketId)"
                  >
                    <div class="font-medium text-gray-800">#{{ result.ticketId }} {{ result.subject ? '- ' + result.subject : '' }}</div>
                    <div class="text-gray-500 truncate">{{ result.requesterName || result.requesterEmail || result.requestSummary || '' }}</div>
                  </div>
                </div>
                <button
                  @click="loadAllSearchedTickets"
                  class="mt-2 w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                >
                  Nacist vsechny ({{ summarySearchResults.length }})
                </button>
              </div>
              <div v-else-if="searchError" class="mt-2 text-sm text-red-600">
                Chyba: {{ searchError }}
              </div>
              <div v-else-if="hasSearched && !isSearchingSummaries" class="mt-2 text-sm text-gray-500">
                Zadne vysledky pro "{{ summarySearchQuery }}"
              </div>
            </div>

            <!-- Freshdesk Filter - Separate -->
            <div class="bg-white rounded-xl border border-gray-200 shadow-lg p-5">
              <h3 class="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Freshdesk filtr
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <!-- Date range -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Obdobi</label>
                  <select v-model="filters.dateRange" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="today">Dnes</option>
                    <option value="yesterday_today">Vcera + Dnes</option>
                    <option value="days7">-7 dni</option>
                    <option value="days14">-14 dni</option>
                    <option value="days30">-30 dni</option>
                    <option value="days90">-90 dni</option>
                    <option value="year">Od zacatku roku</option>
                    <option value="custom">Vlastni datum</option>
                  </select>
                </div>

                <!-- Custom date (shown if custom selected) -->
                <div v-if="filters.dateRange === 'custom'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Od data</label>
                  <input type="date" v-model="filters.customDate" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                </div>

                <!-- Status -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select v-model="filters.status" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="all">Vsechny</option>
                    <option value="new">Nove (vytvorene v obdobi)</option>
                    <option value="open">Otevrene</option>
                    <option value="pending">Cekajici</option>
                    <option value="unresolved">Nevyresene (Otevrene + Cekajici)</option>
                    <option value="resolved">Vyresene</option>
                    <option value="closed">Uzavrene</option>
                  </select>
                </div>

                <!-- Agent -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Technik</label>
                  <select v-model="filters.agent" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500">
                    <option value="all">Vsichni</option>
                    <option v-if="loadingAgents" disabled>Nacitam techniky...</option>
                    <option v-for="agent in agentsList" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
                    <option value="unassigned">Neprirazene</option>
                  </select>
                </div>
              </div>

              <!-- Quick filters -->
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Rychle filtry (klikni pro nacteni)</label>
                <div class="flex flex-wrap gap-2">
                  <button
                    @click="applyQuickFilter('new_today')"
                    class="px-3 py-1.5 text-sm rounded-full border transition"
                    :class="quickFilter === 'new_today' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'"
                  >
                    Nove dnes
                  </button>
                  <button
                    @click="applyQuickFilter('new_week')"
                    class="px-3 py-1.5 text-sm rounded-full border transition"
                    :class="quickFilter === 'new_week' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'"
                  >
                    Nove za tyden
                  </button>
                  <button
                    @click="applyQuickFilter('unassigned')"
                    class="px-3 py-1.5 text-sm rounded-full border transition"
                    :class="quickFilter === 'unassigned' ? 'bg-red-100 border-red-500 text-red-700' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'"
                  >
                    Neprirazene
                  </button>
                  <button
                    @click="applyQuickFilter('urgent')"
                    class="px-3 py-1.5 text-sm rounded-full border transition"
                    :class="quickFilter === 'urgent' ? 'bg-orange-100 border-orange-500 text-orange-700' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'"
                  >
                    Urgentni
                  </button>
                  <button
                    @click="applyQuickFilter('old_open')"
                    class="px-3 py-1.5 text-sm rounded-full border transition"
                    :class="quickFilter === 'old_open' ? 'bg-yellow-100 border-yellow-500 text-yellow-700' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'"
                  >
                    Stare otevrene (30+ dni)
                  </button>
                  <button
                    @click="applyQuickFilter('due_today')"
                    class="px-3 py-1.5 text-sm rounded-full border transition"
                    :class="quickFilter === 'due_today' ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'"
                  >
                    Termin dnes
                  </button>
                </div>
              </div>

              <button
                @click="fetchTickets"
                class="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center justify-center gap-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Nacist tickety
              </button>
            </div>
            </div>
          </div>

          <!-- Tickets Tab Content -->
          <div
            v-else-if="activeTab === 'tickets'"
            class="flex-1 overflow-auto p-6"
          >
            <!-- All Tickets (when filtered) or Unresolved -->
            <div v-if="analysisData.total > 0" class="bg-white rounded-xl border border-gray-200 p-4 mb-6">
              <!-- Header with title and count -->
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-3">
                  <h3 class="text-lg font-semibold text-gray-800">
                    {{ showAllTickets ? 'Vsechny tickety' : 'Nevyresene tickety' }}
                  </h3>
                  <span class="text-sm text-gray-500">
                    {{ filteredTickets.length > 0 ? `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, filteredTickets.length)} z ${filteredTickets.length}` : '0' }}
                  </span>
                  <span v-if="headerFilter" class="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                    {{ headerFilter === 'new_today' ? 'nove dnes' : headerFilter === 'updated_today' ? 'aktualizovane' : 'uzavrene' }}
                    <button @click="headerFilter = null" class="ml-1 text-green-800 hover:text-green-900">&times;</button>
                  </span>
                  <span v-if="quickFilter === 'due_today'" class="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                    termin dnes
                    <button @click="quickFilter = null" class="ml-1 text-purple-800 hover:text-purple-900">&times;</button>
                  </span>
                </div>
              </div>

              <!-- Inline filter bar -->
              <div class="flex flex-wrap items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <!-- Fulltext search -->
                <div class="flex-1 min-w-[200px]">
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Hledat (predmet, email, zakaznik, text)..."
                    class="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <!-- Status filter -->
                <select
                  v-model="inlineStatus"
                  class="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="all">Vsechny statusy</option>
                  <option value="open">Otevrene</option>
                  <option value="pending">Cekajici</option>
                  <option value="resolved">Vyresene</option>
                  <option value="closed">Uzavrene</option>
                </select>
                <!-- Agent filter -->
                <select
                  v-model="inlineAgent"
                  class="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="all">Vsichni agenti</option>
                  <option v-for="agent in agentsList" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
                  <option value="unassigned">Neprirazene</option>
                </select>
                <!-- Clear filters -->
                <button
                  v-if="searchQuery || inlineStatus !== 'all' || inlineAgent !== 'all'"
                  @click="searchQuery = ''; inlineStatus = 'all'; inlineAgent = 'all'"
                  class="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
                >
                  Zrusit filtry
                </button>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-gray-200">
                      <th
                        @click="toggleSort('newest')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          ID
                          <svg v-if="sortBy === 'newest'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('subject')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Predmet
                          <svg v-if="sortBy === 'subject'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('status')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Status
                          <svg v-if="sortBy === 'status'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('category')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Kategorie
                          <svg v-if="sortBy === 'category'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('customer')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Zakaznik
                          <svg v-if="sortBy === 'customer'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('agent')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Agent
                          <svg v-if="sortBy === 'agent'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('activity')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Vytvoreno
                          <svg v-if="sortBy === 'activity'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('updated')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Zmena
                          <svg v-if="sortBy === 'updated'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th
                        @click="toggleSort('age')"
                        class="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none"
                      >
                        <span class="flex items-center gap-1">
                          Stari
                          <svg v-if="sortBy === 'age'" class="w-3 h-3" :class="sortDirection === 'desc' ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </th>
                      <th class="text-left py-2 px-3 font-medium text-gray-600">Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Empty state -->
                    <tr v-if="filteredTickets.length === 0">
                      <td colspan="8" class="py-8 text-center text-gray-500">
                        <div class="flex flex-col items-center gap-2">
                          <svg class="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span class="font-medium text-gray-600">Pro predchozi hledani se nic nenaslo</span>
                          <span v-if="quickFilter === 'due_today'" class="text-xs text-gray-400">
                            Zadny ticket nema termin nastaveny na dnes
                          </span>
                          <span v-else class="text-xs text-gray-400">
                            Zkuste zmenit filtry nebo vyhledavaci dotaz
                          </span>
                          <button
                            @click="showFilterPanel = true; quickFilter = null"
                            class="mt-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                          >
                            Otevrit filtr
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-for="ticket in ticketsToShow"
                      :key="ticket.id"
                      class="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td class="py-2 px-3">
                        <span class="flex items-center gap-1">
                          <span
                            class="text-emerald-600 font-medium hover:underline cursor-pointer"
                            @click="openTicketExternal(ticket.id)"
                            title="Otevrit ve Freshdesku"
                          >
                            #{{ ticket.id }}
                          </span>
                          <svg
                            v-if="ticket.hasConversations"
                            class="w-4 h-4"
                            :class="ticket.onlyAiSummary ? 'text-red-500' : 'text-blue-500'"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            :title="ticket.onlyAiSummary ? 'Pouze AI souhrn' : 'Ma navazujici komunikaci'"
                          >
                            <path fill-rule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clip-rule="evenodd"/>
                          </svg>
                          <!-- Red bubble for saved AI summary -->
                          <span
                            v-if="ticket.hasSavedSummary"
                            class="w-2 h-2 bg-red-500 rounded-full"
                            title="Ma ulozeny AI souhrn"
                          ></span>
                        </span>
                      </td>
                      <td
                        class="py-2 px-3 text-gray-800 max-w-xs truncate hover:text-blue-600 cursor-pointer"
                        :title="ticket.subject"
                        @click="openTicketDetail(ticket)"
                      >{{ ticket.subject }}</td>
                      <td class="py-2 px-3">
                        <span
                          class="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
                          :class="STATUS_CLASS[ticket.status]"
                        >
                          {{ ticket.statusName }}
                        </span>
                      </td>
                      <td class="py-2 px-3">
                        <span
                          v-if="ticket.category"
                          :class="[ticket.category.color, ticket.category.level === 1 ? 'text-white' : '']"
                          class="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
                        >
                          {{ ticket.category.level === 1 ? 'Urgent' : ticket.category.level === 2 ? 'Porad.' : 'Ostatni' }}
                        </span>
                      </td>
                      <td class="py-2 px-3 max-w-[150px] truncate">
                        <button
                          @click.stop="openCustomerTickets(ticket.requester, ticket.requester_email)"
                          class="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          :title="ticket.requester_email || ticket.requester"
                        >
                          {{ ticket.requester }}
                        </button>
                      </td>
                      <td class="py-2 px-3 text-gray-600 max-w-[100px] truncate" :title="ticket.agent">{{ ticket.agent }}</td>
                      <td class="py-2 px-3 text-gray-600 text-xs">{{ ticket.created }}</td>
                      <td class="py-2 px-3 text-gray-600 text-xs">{{ ticket.updated || '-' }}</td>
                      <td class="py-2 px-3">
                        <span
                          :class="ticket.age > 30 ? 'text-red-600 font-semibold' : 'text-gray-600'"
                        >
                          {{ ticket.age }}d
                        </span>
                      </td>
                      <td class="py-2 px-3">
                        <button
                          @click="openTicketDetail(ticket)"
                          class="text-blue-600 hover:text-blue-800 text-xs underline"
                        >
                          Detail
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Pagination controls -->
              <div v-if="totalPages > 1" class="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
                <span class="text-sm text-gray-500">
                  Stranka {{ currentPage }} z {{ totalPages }}
                </span>
                <div class="flex items-center gap-1">
                  <button
                    @click="currentPage = 1"
                    :disabled="currentPage === 1"
                    class="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Prvni stranka"
                  >
                    &laquo;
                  </button>
                  <button
                    @click="currentPage--"
                    :disabled="currentPage === 1"
                    class="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Predchozi
                  </button>
                  <span class="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded">
                    {{ currentPage }}
                  </span>
                  <button
                    @click="currentPage++"
                    :disabled="currentPage === totalPages"
                    class="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Dalsi
                  </button>
                  <button
                    @click="currentPage = totalPages"
                    :disabled="currentPage === totalPages"
                    class="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Posledni stranka"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            </div>

            <!-- Red Flags -->
            <div v-if="analysisData.redFlags.length > 0" class="bg-red-50 rounded-xl border border-red-200 p-4">
              <h3 class="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                Cervene vlajky
              </h3>
              <ul class="space-y-2">
                <li v-for="(flag, index) in analysisData.redFlags" :key="index" class="flex items-start gap-2 text-red-700">
                  <span class="font-bold">!</span>
                  <span>{{ flag }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Tasks Tab Content -->
          <div
            v-else-if="activeTab === 'tasks'"
            class="flex-1 overflow-auto p-6"
          >
            <TechnicianTasksList
              ref="tasksListRef"
              @task-selected="handleTaskSelected"
            />
          </div>

          <!-- Technicians Tab Content -->
          <div
            v-else-if="activeTab === 'technicians'"
            class="flex-1 overflow-auto p-6"
          >
            <TechniciansManager ref="techniciansRef" />
          </div>
        </div>
      </div>

      <!-- Ticket Detail Modal -->
      <Transition name="modal">
        <div
          v-if="selectedTicket"
          class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-1 sm:px-4"
          @click.self="selectedTicket = null"
        >
          <div class="bg-white rounded-xl shadow-2xl w-full max-w-[100vw] sm:max-w-[50rem] h-full max-h-[100vh] sm:max-h-[95vh] overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-gray-100 p-4 border-b flex items-center justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <span class="text-gray-500">#{{ selectedTicket.id }}</span>
                  <span
                    v-if="selectedTicket.category"
                    :class="selectedTicket.category.color"
                    class="px-2 py-0.5 rounded text-xs font-medium text-white"
                  >
                    {{ selectedTicket.category.label }}
                  </span>
                </div>
                <h3 class="font-semibold text-gray-800 mt-1">{{ selectedTicket.subject }}</h3>
              </div>
              <button
                @click="selectedTicket = null"
                class="text-gray-500 hover:text-gray-700 p-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Details -->
            <div class="p-4 border-b">
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="text-gray-500">Zadavatel:</span>
                  <span class="ml-2 text-gray-800">{{ selectedTicket.requester }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Email:</span>
                  <span class="ml-2 text-gray-800">{{ selectedTicket.requester_email || '-' }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Agent:</span>
                  <span class="ml-2 text-gray-800">{{ selectedTicket.agent }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Priorita:</span>
                  <span class="ml-2 text-gray-800">{{ selectedTicket.priorityName }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Vytvoreno:</span>
                  <span class="ml-2 text-gray-800">{{ selectedTicket.created }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Stari:</span>
                  <span class="ml-2" :class="selectedTicket.age > 30 ? 'text-red-600 font-semibold' : 'text-gray-800'">
                    {{ selectedTicket.age }} dni
                  </span>
                </div>
              </div>

              <!-- OZW Search Buttons -->
              <div class="mt-3 pt-3 border-t flex gap-2">
                <!-- Disabled auto-search button -->
                <button
                  disabled
                  class="px-3 py-1.5 text-sm bg-gray-400 text-white rounded-lg cursor-not-allowed flex items-center gap-2 opacity-50"
                  title="Automaticke hledani docasne vypnuto"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Najit v OZW
                </button>

                <!-- Open OZW search with customer name -->
                <button
                  @click="openOzwSearchWithName"
                  class="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Hledat v OZW
                </button>
              </div>
            </div>

            <!-- Description & Conversations (scrollable) -->
            <div class="flex-1 overflow-auto p-4 space-y-4">
              <!-- AI Summary Section (scrolls with content) -->
              <div class="rounded-lg p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-2">
                    <h4 class="text-sm font-semibold text-purple-800 flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      AI Souhrn
                    </h4>
                    <span v-if="existingSummaryDate" class="text-xs text-purple-500">
                      ({{ formatConvDate(existingSummaryDate) }})
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <!-- Warning for new communication -->
                    <span v-if="hasNewCommunication" class="text-xs text-orange-600 flex items-center gap-1">
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                      </svg>
                      Nova komunikace
                    </span>
                    <!-- Generate button - show when no summary -->
                    <button
                      v-if="!aiSummary && !loadingAiSummary"
                      @click="generateAiSummary"
                      class="px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-1"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Vygenerovat souhrn
                    </button>
                    <!-- Regenerate button - show when summary exists and there's new communication -->
                    <button
                      v-if="aiSummary && hasNewCommunication"
                      @click="generateAiSummary"
                      :disabled="loadingAiSummary"
                      class="px-3 py-1 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition flex items-center gap-1 disabled:opacity-50"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Aktualizovat
                    </button>
                    <!-- Small refresh icon - when summary exists but no new communication -->
                    <button
                      v-if="aiSummary && !hasNewCommunication && !loadingAiSummary"
                      @click="generateAiSummary"
                      class="p-1 text-purple-400 hover:text-purple-600 transition"
                      title="Pregenerovat souhrn"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <!-- Token usage display -->
                    <div v-if="aiTokenUsage && aiSummary" class="flex items-center gap-2 text-xs text-purple-400 ml-2 pl-2 border-l border-purple-200">
                      <span class="flex items-center gap-0.5" title="Input tokens">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                        {{ aiTokenUsage.input }}
                      </span>
                      <span class="flex items-center gap-0.5" title="Output tokens">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                        {{ aiTokenUsage.output }}
                      </span>
                      <span v-if="aiTokenUsage.cacheRead > 0" class="flex items-center gap-0.5 text-green-500" title="Cache read">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                        </svg>
                        {{ aiTokenUsage.cacheRead }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Loading state -->
                <div v-if="loadingAiSummary" class="flex items-center gap-2 text-sm text-purple-600">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span>Generuji AI souhrn...</span>
                </div>

                <!-- Error state -->
                <div v-else-if="aiSummaryError" class="text-sm text-red-600 bg-red-50 rounded-lg p-2">
                  Chyba: {{ aiSummaryError }}
                </div>

                <!-- Summary content -->
                <div v-else-if="aiSummary" class="space-y-2 text-sm">
                  <div v-if="aiSummary.request">
                    <span class="font-medium text-gray-700">Pozadavek:</span>
                    <span class="text-gray-600 ml-1">{{ aiSummary.request }}</span>
                  </div>
                  <div v-if="aiSummary.communication">
                    <span class="font-medium text-gray-700">Komunikace:</span>
                    <span class="text-gray-600 ml-1">{{ aiSummary.communication }}</span>
                  </div>
                  <div v-if="aiSummary.agents" class="flex items-center gap-2">
                    <span class="font-medium text-gray-700">Agenti:</span>
                    <span class="text-purple-700 ml-1">{{ aiSummary.agents }}</span>
                  </div>
                  <div v-if="aiSummary.sentiment" class="flex items-center gap-2">
                    <span class="font-medium text-gray-700">Sentiment:</span>
                    <span
                      class="px-2 py-0.5 rounded text-xs font-medium"
                      :class="{
                        'bg-green-100 text-green-800': aiSummary.sentiment.toLowerCase().includes('spokojen') || aiSummary.sentiment.toLowerCase().includes('pozitivn'),
                        'bg-red-100 text-red-800': aiSummary.sentiment.toLowerCase().includes('nespokojen') || aiSummary.sentiment.toLowerCase().includes('frustro') || aiSummary.sentiment.toLowerCase().includes('negativn'),
                        'bg-orange-100 text-orange-800': aiSummary.sentiment.toLowerCase().includes('nalehav') || aiSummary.sentiment.toLowerCase().includes('urgent'),
                        'bg-gray-100 text-gray-800': !aiSummary.sentiment.toLowerCase().includes('spokojen') && !aiSummary.sentiment.toLowerCase().includes('nespokojen') && !aiSummary.sentiment.toLowerCase().includes('nalehav')
                      }"
                    >
                      {{ aiSummary.sentiment }}
                    </span>
                  </div>
                  <div v-if="aiSummary.responseTime" class="bg-blue-50 rounded p-2 border border-blue-100">
                    <span class="font-medium text-blue-800">Odezva:</span>
                    <span class="text-blue-700 ml-1">{{ aiSummary.responseTime }}</span>
                    <!-- Compact metrics -->
                    <div v-if="aiMetrics" class="mt-1.5 pt-1.5 border-t border-blue-200 text-xs space-y-1">
                      <!-- First row: Agent metrics -->
                      <div class="flex flex-wrap gap-3">
                        <span v-if="aiMetrics.firstResponse !== null" class="text-blue-600" title="První odpověď agenta (absolutní / pracovní doba)">
                          1. odpověď: <strong>{{ formatMinutes(aiMetrics.firstResponse) }}</strong>
                          <span v-if="aiMetrics.firstResponseBusiness !== null" class="text-blue-400">({{ formatMinutes(aiMetrics.firstResponseBusiness) }} v PD)</span>
                        </span>
                        <span v-if="aiMetrics.avgAgent !== null" class="text-blue-600" title="Průměrná doba odpovědi agenta (absolutní / pracovní doba)">
                          ⌀ agent: <strong>{{ formatMinutes(aiMetrics.avgAgent) }}</strong>
                          <span v-if="aiMetrics.avgAgentBusiness !== null" class="text-blue-400">({{ formatMinutes(aiMetrics.avgAgentBusiness) }} v PD)</span>
                        </span>
                        <span v-if="aiMetrics.overtimeResponses > 0" class="text-orange-500" title="Odpovědi mimo pracovní dobu">
                          🌙 {{ aiMetrics.overtimeResponses }}× nadpráce
                        </span>
                      </div>
                      <!-- Second row: Customer & total -->
                      <div class="flex flex-wrap gap-3">
                        <span v-if="aiMetrics.avgCustomer !== null" class="text-gray-500" :title="'Průměrná doba odpovědi: ' + (selectedTicket?.requester || 'zákazník')">
                          ⌀ {{ selectedTicket?.requester?.split(' ')[0] || 'zákazník' }}: <strong>{{ formatMinutes(aiMetrics.avgCustomer) }}</strong>
                        </span>
                        <span v-if="aiMetrics.totalResolution !== null" class="text-green-600" title="Celková doba řešení (absolutní / pracovní doba)">
                          Celkem: <strong>{{ aiMetrics.totalResolution }}h</strong>
                          <span v-if="aiMetrics.totalResolutionBusiness !== null" class="text-green-400">({{ aiMetrics.totalResolutionBusiness }}h v PD)</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-if="aiSummary.resolution">
                    <span class="font-medium text-gray-700">Reseni:</span>
                    <span
                      class="ml-1"
                      :class="aiSummary.resolutionMissing ? 'text-red-600 font-semibold bg-red-50 px-1 rounded' : 'text-gray-600'"
                    >
                      {{ aiSummary.resolution }}
                    </span>
                  </div>

                  <!-- Save to Freshdesk button -->
                  <div class="mt-3 pt-2 border-t border-purple-200 flex items-center gap-2">
                    <button
                      v-if="!noteSaved"
                      @click="saveNoteToFreshdesk"
                      :disabled="savingNote"
                      class="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-1 disabled:opacity-50"
                    >
                      <svg v-if="savingNote" class="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      {{ savingNote ? 'Ukladam...' : 'Ulozit do Freshdesku' }}
                    </button>
                    <span v-else class="text-xs text-green-600 flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Ulozeno jako poznamka + tag AI-Souhrn
                    </span>
                  </div>

                  <!-- Conversation Stats -->
                  <div class="mt-2 pt-2 border-t border-purple-200 flex items-center gap-4 text-xs text-gray-600">
                    <span class="flex items-center gap-1" title="Celkem zprav potrebnych k vyreseni">
                      <svg class="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <strong>{{ conversationStats.total }}</strong> zprav
                    </span>
                    <span class="flex items-center gap-1" title="Prichozi (zakaznik)">
                      <svg class="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                      IN: <strong>{{ conversationStats.inbound }}</strong>
                    </span>
                    <span class="flex items-center gap-1" title="Odchozi (agent)">
                      <svg class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                      OUT: <strong>{{ conversationStats.outbound }}</strong>
                    </span>
                    <span v-if="conversationStats.notes > 0" class="flex items-center gap-1" title="Interni poznamky">
                      <svg class="w-3.5 h-3.5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Pozn: <strong>{{ conversationStats.notes }}</strong>
                    </span>
                    <span v-if="existingSummaryDate" class="flex items-center gap-1 text-purple-600" title="AI souhrn nacten z cache">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                      </svg>
                      cache
                    </span>
                  </div>

                  <!-- WhatsApp format for technician -->
                  <div class="mt-3 pt-3 border-t border-purple-200">
                    <button
                      @click="showWhatsappFormat = !showWhatsappFormat"
                      class="flex items-center gap-2 text-xs text-green-600 hover:text-green-700 font-medium"
                    >
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {{ showWhatsappFormat ? 'Skryt' : 'Pro WhatsApp' }}
                      <svg class="w-3 h-3 transition-transform" :class="showWhatsappFormat ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    <div v-if="showWhatsappFormat" class="mt-2 space-y-2">
                      <!-- WhatsApp message preview -->
                      <div class="bg-green-50 border border-green-200 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap text-gray-700">{{ whatsappMessage }}</div>

                      <!-- Copy buttons -->
                      <div class="flex gap-2">
                        <button
                          @click="copyWhatsappMessage(false)"
                          class="flex-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-1"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          </svg>
                          Bez kontaktu
                        </button>
                        <button
                          @click="copyWhatsappMessage(true)"
                          class="flex-1 px-3 py-1.5 text-xs bg-green-700 text-white rounded-lg hover:bg-green-800 transition flex items-center justify-center gap-1"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          </svg>
                          S kontaktem
                        </button>
                      </div>

                      <!-- Contact info separately -->
                      <div v-if="selectedTicket?.requester || selectedTicket?.requester_email || selectedTicket?.phone" class="bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs">
                        <div class="font-medium text-gray-600 mb-1">Kontakt:</div>
                        <div class="text-gray-700">
                          <span v-if="selectedTicket?.requester">{{ selectedTicket.requester }}</span>
                          <span v-if="selectedTicket?.phone" class="ml-2">📞 {{ selectedTicket.phone }}</span>
                          <span v-if="selectedTicket?.requester_email" class="ml-2 text-gray-500">{{ selectedTicket.requester_email }}</span>
                        </div>
                      </div>

                      <!-- Copied notification -->
                      <div v-if="whatsappCopied" class="text-xs text-green-600 flex items-center gap-1">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Zkopirováno do schránky
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty state -->
                <div v-else class="text-xs text-purple-500 italic">
                  Kliknutim na tlacitko vygenerujete AI souhrn ticketu
                </div>
              </div>
              <!-- Original ticket description -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center gap-2 mb-2">
                  <span class="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                    {{ selectedTicket.requester?.charAt(0) || '?' }}
                  </span>
                  <div>
                    <div class="font-medium text-gray-800">{{ selectedTicket.requester }}</div>
                    <div class="text-xs text-gray-500">{{ selectedTicket.created }}</div>
                  </div>
                </div>
                <div
                  class="text-sm text-gray-700 ticket-content"
                  v-html="formatDescription(selectedTicket.description)"
                ></div>
              </div>

              <!-- Loading conversations -->
              <div v-if="loadingConversations" class="flex items-center justify-center py-4">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span class="ml-2 text-sm text-gray-500">Nacitam konverzaci...</span>
              </div>

              <!-- Conversations -->
              <div v-for="conv in ticketConversations" :key="conv.id" class="rounded-lg p-4 border"
                :class="{
                  'bg-green-50 border-green-200': conv.type === 'reply_agent',
                  'bg-gray-50 border-gray-200': conv.type === 'reply_customer',
                  'bg-yellow-50 border-yellow-200': conv.type === 'note'
                }"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                    :class="{
                      'bg-green-500': conv.type === 'reply_agent',
                      'bg-gray-500': conv.type === 'reply_customer',
                      'bg-yellow-500': conv.type === 'note'
                    }"
                  >
                    {{ conv.type === 'reply_agent' ? 'A' : conv.type === 'note' ? 'N' : 'Z' }}
                  </span>
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-800">
                        {{ conv.type === 'reply_customer' ? 'Zakaznik' : (conv.agentName || (conv.type === 'note' ? 'Poznamka' : 'Agent')) }}
                      </span>
                      <span v-if="conv.type === 'note'" class="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">interni</span>
                    </div>
                    <div class="text-xs text-gray-500">{{ formatConvDate(conv.createdAt) }}</div>
                  </div>
                </div>
                <div
                  class="text-sm text-gray-700 ticket-content"
                  v-html="formatDescription(conv.bodyHtml || conv.body)"
                ></div>
              </div>

              <!-- No conversations -->
              <div v-if="!loadingConversations && ticketConversations.length === 0" class="text-center py-4 text-gray-500 text-sm">
                Zadna dalsi komunikace
              </div>
            </div>

            <!-- Footer -->
            <div class="p-4 border-t bg-gray-50 flex justify-between">
              <button
                @click="openTaskAssignment"
                class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Priradit technikovi
              </button>
              <div class="flex gap-2">
                <button
                  @click="openTicketExternal(selectedTicket.id)"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Otevrit ve Freshdesku
                </button>
                <button
                  @click="selectedTicket = null"
                  class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                >
                  Zavrit
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Customer Tickets Popup -->
      <Transition name="fade">
        <div
          v-if="showCustomerPopup"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          @click.self="closeCustomerPopup"
        >
          <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="p-4 border-b bg-blue-50 flex items-center justify-between">
              <div>
                <h3 class="text-lg font-bold text-gray-800">
                  Tickety zakaznika: {{ selectedCustomer?.name }}
                </h3>
                <p class="text-sm text-gray-600">{{ selectedCustomer?.email }}</p>
              </div>
              <button
                @click="closeCustomerPopup"
                class="p-2 hover:bg-blue-100 rounded-lg transition"
              >
                <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-auto p-4">
              <!-- Loading state -->
              <div v-if="loadingCustomerTickets" class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span class="ml-3 text-gray-500">Nacitam tickety zakaznika...</span>
              </div>
              <!-- No tickets -->
              <div v-else-if="customerTickets.length === 0" class="text-center text-gray-500 py-8">
                Zadne tickety k zobrazeni
              </div>
              <!-- Tickets list -->
              <div v-else class="space-y-2">
                <div
                  v-for="ticket in customerTickets"
                  :key="ticket.id"
                  class="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition"
                  @click="openTicketDetail(ticket); closeCustomerPopup()"
                >
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-blue-600 font-medium">#{{ ticket.id }}</span>
                    <span
                      class="px-2 py-0.5 rounded text-xs font-medium"
                      :class="STATUS_CLASS[ticket.status]"
                    >
                      {{ ticket.statusName }}
                    </span>
                  </div>
                  <p class="text-sm text-gray-800 font-medium truncate">{{ ticket.subject }}</p>
                  <div class="flex items-center gap-4 mt-1 text-xs text-gray-500">
                    <span>{{ ticket.created }}</span>
                    <span>Agent: {{ ticket.agent }}</span>
                    <span :class="ticket.age > 30 ? 'text-red-600 font-semibold' : ''">{{ ticket.age }}d</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="p-4 border-t bg-gray-50 flex justify-between items-center">
              <span class="text-sm text-gray-600">Celkem {{ customerTickets.length }} ticketu</span>
              <button
                @click="closeCustomerPopup"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
              >
                Zavrit
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Task Assignment Modal -->
      <Transition name="fade">
        <div
          v-if="showTaskAssignment"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
          @click.self="closeTaskAssignment"
        >
          <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="p-4 border-b bg-orange-50 flex items-center justify-between">
              <div>
                <h3 class="text-lg font-bold text-gray-800">Priradit technikovi</h3>
                <p class="text-sm text-gray-600">Ticket #{{ selectedTicket?.id }}</p>
              </div>
              <button
                @click="closeTaskAssignment"
                class="p-2 hover:bg-orange-100 rounded-lg transition"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Form -->
            <div class="p-4 overflow-y-auto flex-1 space-y-4">
              <!-- Success message -->
              <div v-if="taskSaved" class="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-center">
                <svg class="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                Ukol byl uspesne vytvoren!
              </div>

              <template v-else>
                <!-- Technician select -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Technik *</label>
                  <select
                    v-model="taskAssignmentData.technicianId"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">-- Vyberte technika --</option>
                    <option v-for="tech in techniciansForAssignment" :key="tech.id" :value="tech.id">
                      {{ tech.name }} ({{ tech.email }})
                    </option>
                  </select>
                  <p v-if="techniciansForAssignment.length === 0" class="mt-1 text-xs text-red-500">
                    Zadni technici. Nejdrive je vytvorte v nastaveni.
                  </p>
                </div>

                <!-- Priority -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Priorita</label>
                  <div class="flex gap-2">
                    <button
                      v-for="p in ['low', 'normal', 'high', 'urgent']"
                      :key="p"
                      type="button"
                      @click="taskAssignmentData.priority = p"
                      :class="[
                        'px-3 py-1.5 rounded-lg text-sm border transition',
                        taskAssignmentData.priority === p
                          ? p === 'urgent' ? 'bg-red-600 text-white border-red-600'
                            : p === 'high' ? 'bg-orange-500 text-white border-orange-500'
                            : p === 'normal' ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-gray-500 text-white border-gray-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      ]"
                    >
                      {{ p === 'low' ? 'Nizka' : p === 'normal' ? 'Normalni' : p === 'high' ? 'Vysoka' : 'Urgentni' }}
                    </button>
                  </div>
                </div>

                <!-- Description -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Popis ukolu</label>
                  <textarea
                    v-model="taskAssignmentData.description"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Co ma technik udelat..."
                  ></textarea>
                </div>

                <!-- Customer info section -->
                <div class="border-t pt-4">
                  <h4 class="text-sm font-medium text-gray-700 mb-3">Informace o zakaznikovi</h4>
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="block text-xs text-gray-500 mb-1">Jmeno</label>
                      <input
                        v-model="taskAssignmentData.customerName"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-gray-500 mb-1">Telefon</label>
                      <input
                        v-model="taskAssignmentData.customerPhone"
                        type="tel"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="+420..."
                      />
                    </div>
                    <div class="col-span-2">
                      <label class="block text-xs text-gray-500 mb-1">Email</label>
                      <input
                        v-model="taskAssignmentData.customerEmail"
                        type="email"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div class="col-span-2">
                      <label class="block text-xs text-gray-500 mb-1">Adresa</label>
                      <textarea
                        v-model="taskAssignmentData.customerAddress"
                        rows="2"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Ulice, mesto, PSC..."
                      ></textarea>
                    </div>
                  </div>
                </div>

                <!-- AI Summary note -->
                <div v-if="aiSummary" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <div class="flex items-center gap-2 text-green-700 font-medium mb-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    AI souhrn bude pridan k ukolu
                  </div>
                  <p class="text-green-600 text-xs">Technik dostane souhrn komunikace v emailu.</p>
                </div>

                <!-- AI Request Generation -->
                <div class="border-t pt-4">
                  <div class="flex items-center justify-between mb-2">
                    <h4 class="text-sm font-medium text-gray-700">AI Poptavka pro technika</h4>
                    <button
                      @click="generateTaskRequestForCreation"
                      :disabled="generatingTaskRequestForCreation || !taskAssignmentData.technicianId"
                      class="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-xs disabled:opacity-50 flex items-center gap-2"
                    >
                      <svg v-if="generatingTaskRequestForCreation" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      {{ generatingTaskRequestForCreation ? 'Generuji...' : 'Vygenerovat' }}
                    </button>
                  </div>
                  <p class="text-xs text-gray-500 mb-2">AI vygeneruje detailni poptavku vcetne casove osy, doporuceni a instrukci.</p>

                  <!-- Error -->
                  <div v-if="taskRequestErrorForCreation" class="text-sm text-red-600 bg-red-50 rounded-lg p-2 mb-2">
                    {{ taskRequestErrorForCreation }}
                  </div>

                  <!-- Generated Request Preview -->
                  <div v-if="generatedTaskRequestForCreation" class="bg-orange-50 rounded-lg border border-orange-200">
                    <div class="p-2 border-b border-orange-200 flex items-center justify-between">
                      <span class="text-xs font-medium text-orange-800">Vygenerovana poptavka</span>
                      <button
                        @click="generatedTaskRequestForCreation = null"
                        class="text-orange-600 hover:text-orange-800"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div class="p-2 max-h-40 overflow-y-auto">
                      <pre class="text-xs text-gray-700 whitespace-pre-wrap font-sans">{{ generatedTaskRequestForCreation }}</pre>
                    </div>
                  </div>

                  <div v-else class="p-3 bg-gray-50 rounded-lg text-xs text-gray-500 text-center">
                    Kliknutim na "Vygenerovat" vytvorite AI poptavku pro technika
                  </div>
                </div>
              </template>
            </div>

            <!-- Footer -->
            <div v-if="!taskSaved" class="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                @click="closeTaskAssignment"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
              >
                Zrusit
              </button>
              <button
                @click="saveTaskAssignment"
                :disabled="savingTask || !taskAssignmentData.technicianId"
                class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg v-if="savingTask" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ savingTask ? 'Ukladam...' : 'Vytvorit ukol' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Task Detail Modal -->
      <Transition name="modal">
        <div
          v-if="showTaskDetail && selectedTask"
          class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 px-4"
          @click.self="closeTaskDetail"
        >
          <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-orange-600 text-white p-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 class="font-semibold text-lg">Detail ukolu</h3>
                <p class="text-orange-100 text-sm">#{{ selectedTask.ticket_id }} - {{ selectedTask.technician_name }}</p>
              </div>
              <button @click="closeTaskDetail" class="text-white hover:bg-white/20 rounded-full p-2 transition">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-4 space-y-4">
              <!-- Task info -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-gray-500 mb-1">Status</label>
                  <span
                    :class="{
                      'bg-yellow-100 text-yellow-800': selectedTask.status === 'pending',
                      'bg-blue-100 text-blue-800': selectedTask.status === 'sent',
                      'bg-orange-100 text-orange-800': selectedTask.status === 'in_progress',
                      'bg-green-100 text-green-800': selectedTask.status === 'completed',
                      'bg-gray-100 text-gray-800': selectedTask.status === 'cancelled'
                    }"
                    class="px-2 py-1 rounded text-sm"
                  >
                    {{ selectedTask.status === 'pending' ? 'Ceka' :
                       selectedTask.status === 'sent' ? 'Odeslano' :
                       selectedTask.status === 'in_progress' ? 'Probiha' :
                       selectedTask.status === 'completed' ? 'Dokonceno' :
                       selectedTask.status === 'cancelled' ? 'Zruseno' : selectedTask.status }}
                  </span>
                </div>
                <div>
                  <label class="block text-xs text-gray-500 mb-1">Priorita</label>
                  <span
                    :class="{
                      'text-gray-600': selectedTask.priority === 'low',
                      'text-blue-600': selectedTask.priority === 'normal',
                      'text-orange-600': selectedTask.priority === 'high',
                      'text-red-600': selectedTask.priority === 'urgent'
                    }"
                    class="font-medium"
                  >
                    {{ selectedTask.priority === 'low' ? 'Nizka' :
                       selectedTask.priority === 'normal' ? 'Normalni' :
                       selectedTask.priority === 'high' ? 'Vysoka' :
                       selectedTask.priority === 'urgent' ? 'Urgentni' : selectedTask.priority }}
                  </span>
                </div>
              </div>

              <!-- Subject -->
              <div>
                <label class="block text-xs text-gray-500 mb-1">Predmet ticketu</label>
                <p class="text-gray-900 font-medium">{{ selectedTask.ticket_subject || 'Bez predmetu' }}</p>
              </div>

              <!-- Description -->
              <div v-if="selectedTask.description">
                <label class="block text-xs text-gray-500 mb-1">Popis ukolu</label>
                <p class="text-gray-700 whitespace-pre-wrap">{{ selectedTask.description }}</p>
              </div>

              <!-- Customer -->
              <div v-if="selectedTask.customer_name || selectedTask.customer_email" class="bg-gray-50 rounded-lg p-3">
                <label class="block text-xs text-gray-500 mb-2">Zakaznik</label>
                <div class="space-y-1 text-sm">
                  <p v-if="selectedTask.customer_name" class="font-medium text-gray-900">{{ selectedTask.customer_name }}</p>
                  <p v-if="selectedTask.customer_email" class="text-gray-600">{{ selectedTask.customer_email }}</p>
                  <p v-if="selectedTask.customer_phone" class="text-gray-600">{{ selectedTask.customer_phone }}</p>
                  <p v-if="selectedTask.customer_address" class="text-gray-600 whitespace-pre-wrap">{{ selectedTask.customer_address }}</p>
                </div>
              </div>

              <!-- AI Summary -->
              <div v-if="selectedTask.ai_summary" class="bg-green-50 rounded-lg p-3 border border-green-200">
                <label class="block text-xs text-green-700 mb-2 font-medium">AI Souhrn</label>
                <div class="text-sm text-green-800 space-y-2">
                  <p v-if="selectedTask.ai_summary.shortSummary">{{ selectedTask.ai_summary.shortSummary }}</p>
                  <div v-if="selectedTask.ai_summary.recommendedActions?.length > 0">
                    <p class="font-medium mt-2">Doporucene akce:</p>
                    <ul class="list-disc list-inside">
                      <li v-for="action in selectedTask.ai_summary.recommendedActions" :key="action">{{ action }}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Generate Task Request Section -->
              <div v-if="['pending', 'sent'].includes(selectedTask.status)" class="border-t pt-4">
                <div class="flex items-center justify-between mb-3">
                  <h4 class="font-semibold text-gray-800">Predat ukol technikovi</h4>
                  <button
                    @click="generateTaskRequest"
                    :disabled="generatingTaskRequest"
                    class="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg v-if="generatingTaskRequest" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    {{ generatingTaskRequest ? 'Generuji...' : 'Vygenerovat poptavku' }}
                  </button>
                </div>

                <!-- Error -->
                <div v-if="taskRequestError" class="text-sm text-red-600 bg-red-50 rounded-lg p-2 mb-3">
                  Chyba: {{ taskRequestError }}
                </div>

                <!-- Generated Request Preview -->
                <div v-if="generatedTaskRequest" class="bg-orange-50 rounded-lg border border-orange-200">
                  <div class="p-3 border-b border-orange-200 flex items-center justify-between">
                    <span class="text-sm font-medium text-orange-800">Vygenerovana poptavka</span>
                    <div class="flex gap-2">
                      <button
                        @click="copyTaskRequest"
                        class="px-2 py-1 bg-white border border-orange-300 text-orange-700 rounded text-xs hover:bg-orange-100 transition flex items-center gap-1"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Kopirovat
                      </button>
                      <button
                        @click="sendTaskRequestEmail"
                        class="px-2 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 transition flex items-center gap-1"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Odeslat email
                      </button>
                    </div>
                  </div>
                  <div class="p-3 max-h-64 overflow-y-auto">
                    <pre class="text-xs text-gray-700 whitespace-pre-wrap font-sans">{{ generatedTaskRequest }}</pre>
                  </div>
                </div>
              </div>

              <!-- Completion form (only for pending/sent/in_progress tasks) -->
              <div v-if="['pending', 'sent', 'in_progress'].includes(selectedTask.status)" class="border-t pt-4">
                <h4 class="font-semibold text-gray-800 mb-3">Dokoncit ukol</h4>

                <div class="space-y-3">
                  <div>
                    <label class="block text-sm text-gray-700 mb-1">Vysledek</label>
                    <select v-model="taskCompletionData.result" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="completed">Vyreseno</option>
                      <option value="partial">Castecne vyreseno</option>
                      <option value="cannot_fix">Nelze opravit</option>
                      <option value="parts_ordered">Objednany dily</option>
                      <option value="follow_up">Navazuje dalsi navsteva</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm text-gray-700 mb-1">Poznamka</label>
                    <textarea
                      v-model="taskCompletionData.notes"
                      rows="3"
                      class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Co bylo provedeno, zjisteno..."
                    ></textarea>
                  </div>
                </div>
              </div>

              <!-- Completion info (for completed tasks) -->
              <div v-if="selectedTask.status === 'completed'" class="bg-green-50 rounded-lg p-3 border border-green-200">
                <label class="block text-xs text-green-700 mb-2 font-medium">Dokonceni</label>
                <div class="text-sm text-green-800 space-y-1">
                  <p><span class="text-green-600">Vysledek:</span> {{ selectedTask.completion_result }}</p>
                  <p v-if="selectedTask.completion_notes"><span class="text-green-600">Poznamka:</span> {{ selectedTask.completion_notes }}</p>
                  <p v-if="selectedTask.completed_at"><span class="text-green-600">Datum:</span> {{ new Date(selectedTask.completed_at).toLocaleString('cs-CZ') }}</p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="p-4 border-t bg-gray-50 flex justify-between flex-shrink-0">
              <button
                @click="closeTaskDetail"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
              >
                Zavrit
              </button>
              <button
                v-if="['pending', 'sent', 'in_progress'].includes(selectedTask.status)"
                @click="completeTask"
                :disabled="completingTask"
                class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm disabled:opacity-50 flex items-center gap-2"
              >
                <svg v-if="completingTask" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ completingTask ? 'Ukladam...' : 'Dokoncit ukol' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Stats Modal -->
      <Transition name="modal">
        <div
          v-if="showStatsModal"
          class="fixed inset-0 z-[80] flex items-center justify-center bg-black bg-opacity-50 px-4"
          @click.self="showStatsModal = false"
        >
          <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 flex items-center justify-between flex-shrink-0">
              <div class="flex items-center gap-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <div>
                  <h3 class="font-semibold text-lg">Statistiky a Reporty</h3>
                  <p class="text-indigo-100 text-sm">Prehled vykonnosti tymu</p>
                </div>
              </div>
              <button @click="showStatsModal = false" class="text-white hover:bg-white/20 rounded-full p-2 transition">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Tab Navigation -->
            <div class="flex border-b border-gray-200 bg-gray-50 px-4 flex-shrink-0">
              <button @click="statsModalTab = 'overview'" :class="statsModalTab === 'overview' ? 'border-indigo-500 text-indigo-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'" class="px-4 py-3 text-sm font-medium border-b-2 transition-colors">Přehled</button>
              <button @click="statsModalTab = 'daily'" :class="statsModalTab === 'daily' ? 'border-blue-500 text-blue-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'" class="px-4 py-3 text-sm font-medium border-b-2 transition-colors">Denní</button>
              <button @click="statsModalTab = 'monthly'" :class="statsModalTab === 'monthly' ? 'border-purple-500 text-purple-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'" class="px-4 py-3 text-sm font-medium border-b-2 transition-colors">Měsíční</button>
              <button @click="statsModalTab = 'yearly'" :class="statsModalTab === 'yearly' ? 'border-green-500 text-green-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'" class="px-4 py-3 text-sm font-medium border-b-2 transition-colors">Roční</button>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-6 space-y-6">
              <!-- OVERVIEW TAB -->
              <div v-show="statsModalTab === 'overview'" class="space-y-6">
              <!-- Today's Quick Stats (from current filter results) -->
              <div v-if="analysisData" class="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                <h4 class="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Aktualni stav (dle filtru)
                </h4>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div class="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                    <div class="text-2xl font-bold text-gray-800">{{ analysisData.total }}</div>
                    <div class="text-sm text-gray-500">Celkem ticketu</div>
                  </div>
                  <div v-if="analysisData.todayStats" class="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
                    <div class="text-2xl font-bold text-blue-600">{{ analysisData.todayStats.newToday }}</div>
                    <div class="text-sm text-gray-500">Novych dnes</div>
                  </div>
                  <div v-if="analysisData.todayStats" class="bg-white rounded-lg p-3 shadow-sm border border-amber-100">
                    <div class="text-2xl font-bold text-amber-600">{{ analysisData.todayStats.updatedToday }}</div>
                    <div class="text-sm text-gray-500">Aktualizovano dnes</div>
                  </div>
                  <div v-if="analysisData.todayStats" class="bg-white rounded-lg p-3 shadow-sm border border-green-100">
                    <div class="text-2xl font-bold text-green-600">{{ analysisData.todayStats.closedToday }}</div>
                    <div class="text-sm text-gray-500">Uzavreno dnes</div>
                  </div>
                </div>

                <!-- Red Flags -->
                <div v-if="analysisData.redFlags?.length > 0" class="mt-4 bg-red-50 rounded-lg p-3 border border-red-200">
                  <h5 class="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                    Cervene vlajecky ({{ analysisData.redFlags.length }})
                  </h5>
                  <ul class="text-sm text-red-700 space-y-1">
                    <li v-for="(flag, index) in analysisData.redFlags.slice(0, 5)" :key="index" class="flex items-start gap-2">
                      <span class="text-red-400">•</span>
                      {{ flag }}
                    </li>
                    <li v-if="analysisData.redFlags.length > 5" class="text-red-500 italic">
                      ... a dalsich {{ analysisData.redFlags.length - 5 }}
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Daily Report Section -->
              <div class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
                <div class="flex items-center justify-between mb-4">
                  <h4 class="font-semibold text-indigo-800 flex items-center gap-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Denni report
                  </h4>
                  <button
                    @click="loadDailyStats"
                    :disabled="loadingDailyStats"
                    class="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg v-if="loadingDailyStats" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {{ loadingDailyStats ? 'Nacitam...' : (dailyStats ? 'Obnovit' : 'Nacist data') }}
                  </button>
                </div>

                <!-- Loading state -->
                <div v-if="loadingDailyStats" class="flex items-center justify-center py-8">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>

                <!-- Daily stats content -->
                <div v-else-if="dailyStats" class="space-y-4">
                  <!-- Date info -->
                  <div class="text-sm text-indigo-600 font-medium">
                    Statistiky za {{ formatDailyStatsDate(dailyStats.date) }}
                  </div>

                  <!-- Main metrics -->
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="flex items-baseline gap-2">
                        <span class="text-2xl font-bold text-gray-800">{{ dailyStats.stats.totalTickets }}</span>
                        <span
                          v-if="dailyStats.comparison.ticketsChange !== 0"
                          :class="dailyStats.comparison.ticketsChange > 0 ? 'text-red-500' : 'text-green-500'"
                          class="text-sm font-medium"
                        >
                          {{ dailyStats.comparison.ticketsChange > 0 ? '+' : '' }}{{ dailyStats.comparison.ticketsChange }}
                        </span>
                      </div>
                      <div class="text-xs text-gray-500">Aktivnich ticketu</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="flex items-baseline gap-2">
                        <span class="text-2xl font-bold text-blue-600">{{ dailyStats.stats.newTickets }}</span>
                        <span
                          v-if="dailyStats.comparison.newTicketsChange !== 0"
                          :class="dailyStats.comparison.newTicketsChange > 0 ? 'text-red-500' : 'text-green-500'"
                          class="text-sm font-medium"
                        >
                          {{ dailyStats.comparison.newTicketsChange > 0 ? '+' : '' }}{{ dailyStats.comparison.newTicketsChange }}
                        </span>
                      </div>
                      <div class="text-xs text-gray-500">Novych</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="flex items-baseline gap-2">
                        <span class="text-2xl font-bold text-green-600">{{ dailyStats.stats.closedTickets }}</span>
                        <span
                          v-if="dailyStats.comparison.closedChange !== 0"
                          :class="dailyStats.comparison.closedChange > 0 ? 'text-green-500' : 'text-red-500'"
                          class="text-sm font-medium"
                        >
                          {{ dailyStats.comparison.closedChange > 0 ? '+' : '' }}{{ dailyStats.comparison.closedChange }}
                        </span>
                      </div>
                      <div class="text-xs text-gray-500">Uzavrenych</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="text-2xl font-bold text-amber-600">
                        {{ dailyStats.stats.avgFirstResponse ? `${dailyStats.stats.avgFirstResponse} min` : '-' }}
                      </div>
                      <div class="text-xs text-gray-500">Prvm. odpoved</div>
                      <div v-if="dailyStats.comparison.avgResponseChange" class="text-xs" :class="dailyStats.comparison.avgResponseChange < 0 ? 'text-green-500' : 'text-red-500'">
                        {{ dailyStats.comparison.avgResponseChange > 0 ? '+' : '' }}{{ dailyStats.comparison.avgResponseChange }}%
                      </div>
                    </div>
                  </div>

                  <!-- Agent performance -->
                  <div v-if="dailyStats.agentStats?.length > 0" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3">Vykonnost agentu</h5>
                    <div class="space-y-2">
                      <div
                        v-for="agent in dailyStats.agentStats.slice(0, 5)"
                        :key="agent.id"
                        class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                      >
                        <div class="font-medium text-gray-800">{{ agent.name }}</div>
                        <div class="flex items-center gap-4 text-sm">
                          <span class="text-gray-600">{{ agent.ticketsHandled }} ticketu</span>
                          <span class="text-green-600">{{ agent.ticketsClosed }} uzavreno</span>
                          <span v-if="agent.avgResponseTime" class="text-amber-600">{{ agent.avgResponseTime }} min</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Sentiment stats -->
                  <div v-if="dailyStats.sentimentStats" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3">Sentiment zakazniku (z AI souhrnu)</h5>
                    <div class="grid grid-cols-3 md:grid-cols-5 gap-2">
                      <div class="text-center p-2 bg-green-50 rounded-lg">
                        <div class="text-xl font-bold text-green-600">{{ dailyStats.sentimentStats.positive }}</div>
                        <div class="text-xs text-gray-500">Pozitivni</div>
                      </div>
                      <div class="text-center p-2 bg-gray-50 rounded-lg">
                        <div class="text-xl font-bold text-gray-600">{{ dailyStats.sentimentStats.neutral }}</div>
                        <div class="text-xs text-gray-500">Neutralni</div>
                      </div>
                      <div class="text-center p-2 bg-red-50 rounded-lg">
                        <div class="text-xl font-bold text-red-600">{{ dailyStats.sentimentStats.negative }}</div>
                        <div class="text-xs text-gray-500">Negativni</div>
                      </div>
                      <div class="text-center p-2 bg-orange-50 rounded-lg">
                        <div class="text-xl font-bold text-orange-600">{{ dailyStats.sentimentStats.frustrated }}</div>
                        <div class="text-xs text-gray-500">Frustrovani</div>
                      </div>
                      <div class="text-center p-2 bg-purple-50 rounded-lg">
                        <div class="text-xl font-bold text-purple-600">{{ dailyStats.sentimentStats.urgent }}</div>
                        <div class="text-xs text-gray-500">Urgentni</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty state -->
                <div v-else class="text-center py-8 text-gray-500">
                  <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p>Kliknete na "Nacist data" pro zobrazeni dennich statistik</p>
                </div>
              </div>
              </div><!-- END OVERVIEW TAB -->

              <!-- DAILY TAB -->
              <div v-show="statsModalTab === 'daily'" class="space-y-6">
              <!-- Daily Report Section -->
              <div class="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                <div class="flex items-center justify-between mb-4">
                  <h4 class="font-semibold text-blue-800 flex items-center gap-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Denní AI Report
                  </h4>
                  <div class="flex items-center gap-2">
                    <select v-model="dailyReportDate" class="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                      <option v-for="day in recentDays" :key="day.value" :value="day.value">
                        {{ day.label }}
                      </option>
                    </select>
                    <button
                      @click="generateDailyReport"
                      :disabled="generatingDailyReport"
                      class="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                    >
                      <svg v-if="generatingDailyReport" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {{ generatingDailyReport ? 'Generuji...' : 'Generovat' }}
                    </button>
                    <!-- Check changes button -->
                    <button
                      v-if="dailyReportCached && dailyReport?.fingerprint"
                      @click="checkDailyReportChanges"
                      :disabled="checkingDailyReportChanges"
                      class="px-2 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 transition disabled:opacity-50 flex items-center gap-1"
                      title="Zkontrolovat zmeny od vygenerovani"
                    >
                      <svg v-if="checkingDailyReportChanges" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <!-- Load saved daily reports dropdown -->
                    <div class="relative">
                      <button
                        @click="toggleSavedDailyReports"
                        class="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 transition flex items-center gap-2"
                        title="Nacist ulozeny denni report"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Nacist
                      </button>
                      <div
                        v-if="showSavedDailyReportsDropdown"
                        class="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto"
                      >
                        <div class="p-2 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                          <span class="text-sm font-medium text-gray-700">Denni reporty</span>
                          <button @click="showSavedDailyReportsDropdown = false" class="text-gray-400 hover:text-gray-600">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div v-if="loadingSavedDailyReports" class="p-4 text-center text-gray-500 text-sm">
                          Nacitam...
                        </div>
                        <div v-else-if="savedDailyReports.length === 0" class="p-4 text-center text-gray-500 text-sm">
                          Zadne ulozene denni reporty
                        </div>
                        <div v-else>
                          <button
                            v-for="report in savedDailyReports"
                            :key="report.id"
                            @click="loadSavedDailyReport(report.periodKey)"
                            class="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                          >
                            <div class="font-medium text-gray-800 flex items-center gap-2">
                              {{ formatDailyReportLabel(report) }}
                              <span v-if="report.unansweredCount > 0" class="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                                {{ report.unansweredCount }} nezodp.
                              </span>
                            </div>
                            <div class="text-xs text-gray-500">
                              {{ report.totalTickets }} ticketu
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Daily Report Content -->
                <div v-if="generatingDailyReport" class="text-center py-4 text-blue-600">
                  <svg class="w-8 h-8 mx-auto animate-spin mb-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generuji denni report...
                </div>
                <div v-else-if="dailyReport" class="space-y-4">
                  <!-- Changes Banner -->
                  <div v-if="dailyReportChanges?.hasChanges" class="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span class="text-amber-800 text-sm">
                        <strong>Zmeny od vygenerovani:</strong> {{ dailyReportChanges.changes?.summary }}
                      </span>
                    </div>
                    <button
                      @click="generateDailyReport"
                      class="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 transition"
                    >
                      Regenerovat
                    </button>
                  </div>
                  <div v-else-if="dailyReportChanges && !dailyReportChanges.hasChanges" class="bg-green-50 border border-green-200 rounded-lg p-2 flex items-center gap-2 text-sm text-green-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Zadne zmeny od vygenerovani
                  </div>

                  <!-- Header: Period + Inline Stats -->
                  <div class="flex items-center justify-between flex-wrap gap-2">
                    <h5 class="font-bold text-lg text-gray-800">{{ dailyReport.period?.label || 'Report' }}</h5>
                    <div class="flex items-center gap-4 text-xs">
                      <span class="text-gray-600" title="Celkem ticketu">{{ dailyReport.volume?.totalTickets || 0 }} celkem</span>
                      <span class="text-blue-600" title="Novych ticketu">{{ dailyReport.volume?.newTickets || 0 }} novych</span>
                      <span class="text-green-600" title="Uzavrenych">{{ dailyReport.volume?.closedTickets || 0 }} uzavr.</span>
                      <span :class="dailyReport.sentiment?.positiveRate >= 50 ? 'text-green-600' : 'text-red-600'" title="Pozitivni sentiment">{{ dailyReport.sentiment?.positiveRate || 0 }}% poz.</span>
                    </div>
                  </div>

                  <!-- Unanswered Tickets Section -->
                  <div v-if="!dailyReport.actionItems?.goalMet" class="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h6 class="font-bold text-red-800 flex items-center gap-2 mb-3">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Bez odpovedi ({{ (dailyReport.actionItems?.unansweredTickets?.length || 0) + (dailyReport.actionItems?.pendingFollowUp?.length || 0) }})
                    </h6>

                    <!-- NEW tickets (no agent response yet) -->
                    <div v-if="dailyReport.actionItems?.unansweredTickets?.length > 0" class="mb-3">
                      <div class="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                        <span class="w-2 h-2 bg-red-500 rounded-full"></span>
                        Nove tickety bez odpovedi:
                      </div>
                      <table class="w-full text-xs">
                        <thead class="text-left text-red-600">
                          <tr>
                            <th class="py-1 pr-2 w-16">ID</th>
                            <th class="py-1 pr-2 w-32">Zadatel</th>
                            <th class="py-1">Popis</th>
                            <th class="py-1 text-right w-12">Cas</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="t in dailyReport.actionItems.unansweredTickets" :key="t.ticketId" class="border-t border-red-100">
                            <td class="py-1.5 pr-2">
                              <a :href="`https://hotjet.freshdesk.com/a/tickets/${t.ticketId}`" target="_blank" class="font-semibold text-red-700 hover:underline">#{{ t.ticketId }}</a>
                            </td>
                            <td class="py-1.5 pr-2 text-red-600 truncate max-w-[120px]" :title="t.requesterName || t.requesterEmail">{{ t.requesterName || t.requesterEmail || '-' }}</td>
                            <td class="py-1.5 text-gray-700 truncate max-w-[200px]" :title="t.subject">{{ t.subject }}</td>
                            <td class="py-1.5 text-right text-red-500 font-medium">{{ t.hoursWithoutResponse }}h</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- RE-RESPONDED tickets (customer wrote again) -->
                    <div v-if="dailyReport.actionItems?.pendingFollowUp?.length > 0">
                      <div class="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                        <span class="w-2 h-2 bg-amber-500 rounded-full"></span>
                        Zakaznik znovu odpovědel:
                      </div>
                      <table class="w-full text-xs">
                        <thead class="text-left text-amber-600">
                          <tr>
                            <th class="py-1 pr-2 w-16">ID</th>
                            <th class="py-1 pr-2 w-32">Zadatel</th>
                            <th class="py-1">Popis</th>
                            <th class="py-1 text-right w-12">Cas</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="t in dailyReport.actionItems.pendingFollowUp" :key="t.ticketId" class="border-t border-amber-100">
                            <td class="py-1.5 pr-2">
                              <a :href="`https://hotjet.freshdesk.com/a/tickets/${t.ticketId}`" target="_blank" class="font-semibold text-amber-700 hover:underline">#{{ t.ticketId }}</a>
                            </td>
                            <td class="py-1.5 pr-2 text-amber-600 truncate max-w-[120px]" :title="t.requesterName || t.requesterEmail">{{ t.requesterName || t.requesterEmail || '-' }}</td>
                            <td class="py-1.5 text-gray-700 truncate max-w-[200px]" :title="t.subject">{{ t.subject }}</td>
                            <td class="py-1.5 text-right text-amber-500 font-medium">{{ t.hoursSinceCustomerResponse }}h</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div v-else class="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div class="flex items-center gap-2 text-green-700 text-sm">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span class="font-medium">Vsechny tickety maji odpoved!</span>
                    </div>
                  </div>

                  <!-- Agent Stats - 2 columns -->
                  <div v-if="dailyReport.agentStats?.length > 0" class="bg-white rounded-lg p-3 shadow-sm">
                    <div class="flex items-center justify-between mb-2">
                      <h6 class="font-semibold text-gray-700 text-sm">Aktivita agentu</h6>
                      <div class="flex items-center gap-3 text-[10px]">
                        <span class="text-gray-400">Př = přiřazeno</span>
                        <span class="text-green-500">Vy = vyřešeno</span>
                        <span class="text-blue-500">Od = odpovězeno</span>
                      </div>
                    </div>
                    <div class="grid grid-cols-2 gap-x-6 gap-y-1">
                      <div v-for="agent in dailyReport.agentStats.filter(a => a.name !== 'Servis')" :key="agent.name" class="flex items-center justify-between text-xs py-1 border-b border-gray-50">
                        <span class="font-medium text-gray-800 truncate max-w-[120px]">{{ agent.name }}</span>
                        <div class="flex items-center gap-1 text-[11px]">
                          <span class="text-gray-500 w-5 text-right">{{ agent.ticketsHandled }}</span>
                          <span class="text-green-600 w-5 text-right">{{ agent.ticketsResolved }}</span>
                          <span class="text-blue-600 w-5 text-right">{{ agent.ticketsResponded }}</span>
                        </div>
                      </div>
                    </div>
                    <!-- Servis summary if exists -->
                    <div v-if="dailyReport.agentStats.find(a => a.name === 'Servis')" class="mt-2 pt-2 border-t border-gray-200">
                      <div class="flex items-center justify-between text-xs">
                        <span class="font-medium text-purple-700">Servis (součet)</span>
                        <div class="flex items-center gap-1 text-[11px]">
                          <span class="text-gray-500 w-5 text-right">{{ dailyReport.agentStats.find(a => a.name === 'Servis')?.ticketsHandled || 0 }}</span>
                          <span class="text-green-600 w-5 text-right">{{ dailyReport.agentStats.find(a => a.name === 'Servis')?.ticketsResolved || 0 }}</span>
                          <span class="text-blue-600 w-5 text-right">{{ dailyReport.agentStats.find(a => a.name === 'Servis')?.ticketsResponded || 0 }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- AI Insights -->
                  <div v-if="dailyReport.insights?.summaryText" class="bg-white rounded-lg p-3 shadow-sm">
                    <h6 class="font-semibold text-gray-700 mb-2 text-sm">AI souhrn dne</h6>
                    <p class="text-xs text-gray-600 italic">{{ dailyReport.insights.summaryText }}</p>
                    <div v-if="dailyReport.insights.urgentAttention?.length > 0" class="mt-2 pt-2 border-t border-gray-100">
                      <div class="text-xs font-semibold text-red-700 mb-1">Potrebuje pozornost:</div>
                      <ul class="text-xs text-red-600 list-disc list-inside">
                        <li v-for="item in dailyReport.insights.urgentAttention" :key="item">{{ item }}</li>
                      </ul>
                    </div>
                  </div>

                  <!-- Technician Highlights -->
                  <div v-if="dailyReport.insights?.agentHighlights?.length > 0" class="bg-white rounded-lg p-3 shadow-sm">
                    <h6 class="font-semibold text-gray-700 mb-2 text-sm flex items-center gap-2">
                      <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Hodnoceni techniku
                    </h6>
                    <div class="space-y-2">
                      <div v-for="tech in dailyReport.insights.agentHighlights" :key="tech.agent" class="bg-gray-50 rounded p-2">
                        <div class="flex items-center justify-between mb-1">
                          <span class="font-medium text-gray-800 text-sm">{{ tech.agent }}</span>
                          <span class="text-xs px-1.5 py-0.5 rounded" :class="tech.sentiment === 'pozitivní' ? 'bg-green-100 text-green-700' : tech.sentiment === 'negativní' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'">
                            {{ tech.sentiment || 'neutrální' }}
                          </span>
                        </div>
                        <div class="flex flex-wrap gap-2 text-xs">
                          <span v-if="tech.wasHelpful" class="text-green-600">✓ Napomocny</span>
                          <span v-if="tech.wasHelpful === false" class="text-red-600">✗ Malo napomocny</span>
                          <span v-if="tech.generalResponse" class="text-amber-600">⚠ Obecna odpoved</span>
                          <span v-if="tech.generalResponse === false" class="text-green-600">✓ Konkretni</span>
                          <span v-if="tech.askedPreciseQuestions" class="text-green-600">✓ Presne dotazy</span>
                          <span v-if="tech.askedPreciseQuestions === false" class="text-amber-600">⚠ Vagne dotazy</span>
                        </div>
                        <p v-if="tech.note" class="text-xs text-gray-500 mt-1 italic">{{ tech.note }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Token usage footer -->
                  <div class="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                    <span v-if="dailyReportCached" class="flex items-center gap-1">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Nacteno z cache
                    </span>
                    <span v-else class="flex items-center gap-1">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Nove vygenerovano
                    </span>
                    <span v-if="dailyReport.tokens" class="text-right">
                      Tokeny: {{ ((dailyReport.tokens?.input || 0) + (dailyReport.tokens?.output || 0)).toLocaleString() }} |
                      Cena: ${{ (dailyReport.tokens?.estimatedCostUsd || 0).toFixed(3) }}
                    </span>
                  </div>
                </div>
                <div v-else class="text-center py-4 text-gray-500">
                  <p class="text-sm">Kliknete na "Generovat" pro vytvoreni denniho AI reportu</p>
                </div>
              </div>
              </div><!-- END DAILY TAB -->

              <!-- MONTHLY TAB -->
              <div v-show="statsModalTab === 'monthly'" class="space-y-6">
              <!-- Monthly Report Section -->
              <div class="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                <div class="flex items-center justify-between mb-4">
                  <h4 class="font-semibold text-purple-800 flex items-center gap-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Mesicni AI Report
                  </h4>
                  <div class="flex items-center gap-2">
                    <select v-model="monthlyReportPeriod" class="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                      <option value="last7days">Poslednich 7 dni</option>
                      <option value="current">Aktualni mesic</option>
                      <option disabled>──────────</option>
                      <option v-for="month in availableMonths" :key="month.value" :value="month.value">
                        {{ month.label }}
                      </option>
                    </select>
                    <button
                      @click="generateMonthlyReport"
                      :disabled="generatingMonthlyReport"
                      class="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition disabled:opacity-50 flex items-center gap-2"
                    >
                      <svg v-if="generatingMonthlyReport" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {{ generatingMonthlyReport ? 'Generuji...' : 'Generovat report' }}
                    </button>
                    <button
                      v-if="monthlyReport"
                      @click="printMonthlyReport"
                      class="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
                      title="Tisknout / Ulozit jako PDF"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      PDF
                    </button>
                    <!-- Load saved reports dropdown -->
                    <div class="relative">
                      <button
                        @click="toggleSavedReports"
                        class="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                        title="Nacist ulozeny report"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Nacist
                      </button>
                      <div
                        v-if="showSavedReportsDropdown"
                        class="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto"
                      >
                        <div class="p-2 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                          <span class="text-sm font-medium text-gray-700">Ulozene reporty</span>
                          <button @click="showSavedReportsDropdown = false" class="text-gray-400 hover:text-gray-600">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div v-if="loadingSavedReports" class="p-4 text-center text-gray-500 text-sm">
                          Nacitam...
                        </div>
                        <div v-else-if="savedReports.length === 0" class="p-4 text-center text-gray-500 text-sm">
                          Zadne ulozene reporty
                        </div>
                        <div v-else>
                          <button
                            v-for="report in savedReports"
                            :key="report.id"
                            @click="loadSavedMonthlyReport(report.id)"
                            class="w-full px-3 py-2 text-left text-sm hover:bg-purple-50 border-b border-gray-100 last:border-0"
                          >
                            <div class="font-medium text-gray-800">{{ report.label }}</div>
                            <div class="text-xs text-gray-500">
                              {{ report.totalTickets }} ticketu | ${{ report.estimatedCostUsd?.toFixed(2) || '0.00' }}
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                    <!-- Generate entire year button -->
                    <button
                      @click="generateEntireYear"
                      :disabled="generatingMonthlyReport || generatingYearlyBatch"
                      class="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 transition disabled:opacity-50 flex items-center gap-2"
                      title="Vygenerovat vsechny chybejici mesice pro rok"
                    >
                      <svg v-if="generatingYearlyBatch" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Cely rok
                    </button>
                  </div>
                </div>

                <!-- Yearly Batch Progress -->
                <div v-if="generatingYearlyBatch" class="mb-4 bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-amber-800 font-medium">
                      Generuji rok {{ new Date().getFullYear() }}
                    </span>
                    <span class="text-amber-700 text-sm">
                      Mesic {{ yearlyBatchProgress.currentMonthIndex }} / {{ yearlyBatchProgress.totalMonths }}
                    </span>
                  </div>
                  <!-- Overall progress -->
                  <div class="mb-3">
                    <div class="flex justify-between text-xs text-amber-600 mb-1">
                      <span>Celkovy postup</span>
                      <span>{{ yearlyBatchProgress.overallProgress }}%</span>
                    </div>
                    <div class="h-3 bg-amber-200 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
                        :style="{ width: `${yearlyBatchProgress.overallProgress}%` }"
                      ></div>
                    </div>
                  </div>
                  <!-- Current month progress -->
                  <div class="bg-white rounded-lg p-3 border border-amber-100">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-purple-700 font-medium">{{ yearlyBatchProgress.currentMonth }}</span>
                      <span class="text-purple-600 text-sm">{{ yearlyBatchProgress.currentMonthProgress }}%</span>
                    </div>
                    <div class="h-2 bg-purple-100 rounded-full overflow-hidden mb-2">
                      <div
                        class="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                        :style="{ width: `${yearlyBatchProgress.currentMonthProgress}%` }"
                      ></div>
                    </div>
                    <div class="flex justify-between text-xs text-gray-600">
                      <span>
                        {{ yearlyBatchProgress.processedTickets }} / {{ yearlyBatchProgress.totalTickets }} ticketu
                        <span v-if="yearlyBatchProgress.currentTicketId" class="ml-2 text-purple-600 font-mono">
                          (posledni: #{{ yearlyBatchProgress.currentTicketId }})
                        </span>
                      </span>
                      <span class="text-amber-600">
                        ~${{ yearlyBatchProgress.estimatedCostUsd.toFixed(3) }}
                      </span>
                    </div>
                  </div>
                  <!-- Processed months -->
                  <div v-if="yearlyBatchProgress.monthsProcessed.length > 0" class="mt-3 flex flex-wrap gap-1">
                    <span
                      v-for="m in yearlyBatchProgress.monthsProcessed"
                      :key="m"
                      class="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs"
                    >
                      {{ m }}
                    </span>
                  </div>
                </div>

                <!-- Progress -->
                <div v-if="generatingMonthlyReport" class="mb-4">
                  <div class="flex justify-between text-sm mb-2">
                    <span class="text-purple-700">{{ monthlyReportProgress.currentStep }}</span>
                    <span class="text-purple-600 font-medium">{{ monthlyReportProgress.progress }}%</span>
                  </div>
                  <div class="h-2 bg-purple-100 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                      :style="{ width: `${monthlyReportProgress.progress}%` }"
                    ></div>
                  </div>
                  <div class="flex justify-between text-xs text-purple-500 mt-2">
                    <span>{{ monthlyReportProgress.processedTickets }} / {{ monthlyReportProgress.totalTickets }} ticketu</span>
                    <span>
                      {{ formatNumber(monthlyReportProgress.tokens.input + monthlyReportProgress.tokens.output) }} tokenu |
                      ~${{ monthlyReportProgress.estimatedCostUsd.toFixed(3) }}
                    </span>
                  </div>
                </div>

                <!-- Report Content -->
                <div v-else-if="monthlyReport" class="space-y-4">
                  <!-- Period header -->
                  <div class="text-sm text-purple-600 font-medium">
                    {{ monthlyReport.period?.label || 'Report' }}
                  </div>

                  <!-- Volume stats -->
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="text-2xl font-bold text-gray-800">{{ monthlyReport.volume?.totalTickets || 0 }}</div>
                      <div class="text-xs text-gray-500">Celkem ticketu</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="text-2xl font-bold text-purple-600">{{ monthlyReport.volume?.processedTickets || 0 }}</div>
                      <div class="text-xs text-gray-500">Zpracovano</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="text-2xl font-bold text-green-600">{{ monthlyReport.sentiment?.positiveRate || 0 }}%</div>
                      <div class="text-xs text-gray-500">Pozitivni</div>
                    </div>
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                      <div class="text-2xl font-bold text-red-600">{{ monthlyReport.sentiment?.negativeRate || 0 }}%</div>
                      <div class="text-xs text-gray-500">Negativni</div>
                    </div>
                  </div>

                  <!-- Lifecycle / Aging Analysis -->
                  <div v-if="monthlyReport.lifecycle" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Zivotni cyklus ticketu
                    </h5>

                    <div class="grid grid-cols-2 gap-4 mb-4">
                      <!-- Closed in period -->
                      <div class="bg-green-50 rounded-lg p-3">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-green-700">Uzavreno v obdobi</span>
                          <span class="text-xl font-bold text-green-600">{{ monthlyReport.lifecycle.closedInPeriod?.total || 0 }}</span>
                        </div>
                        <div class="text-xs text-gray-600 space-y-1">
                          <div class="flex justify-between">
                            <span>Nove vytvorene a uzavrene:</span>
                            <span class="font-medium">{{ monthlyReport.lifecycle.closedInPeriod?.createdInPeriod || 0 }}</span>
                          </div>
                          <div class="flex justify-between">
                            <span>Backlog (stare) uzavreny:</span>
                            <span class="font-medium">{{ monthlyReport.lifecycle.closedInPeriod?.createdBefore?.total || 0 }}</span>
                          </div>
                          <div v-if="monthlyReport.lifecycle.closedInPeriod?.createdBefore?.total > 0" class="pl-2 border-l-2 border-green-200 mt-1">
                            <div class="flex justify-between text-gray-500">
                              <span>do 14 dni:</span>
                              <span>{{ monthlyReport.lifecycle.closedInPeriod?.createdBefore?.within14days || 0 }}</span>
                            </div>
                            <div class="flex justify-between text-gray-500">
                              <span>14-30 dni:</span>
                              <span>{{ monthlyReport.lifecycle.closedInPeriod?.createdBefore?.within30days || 0 }}</span>
                            </div>
                            <div class="flex justify-between text-gray-500">
                              <span>30-60 dni:</span>
                              <span>{{ monthlyReport.lifecycle.closedInPeriod?.createdBefore?.within60days || 0 }}</span>
                            </div>
                            <div class="flex justify-between text-orange-600">
                              <span>90+ dni:</span>
                              <span>{{ monthlyReport.lifecycle.closedInPeriod?.createdBefore?.over90days || 0 }}</span>
                            </div>
                          </div>
                          <div class="flex justify-between pt-1 border-t border-green-200">
                            <span>Ø doba reseni:</span>
                            <span class="font-medium">{{ monthlyReport.lifecycle.closedInPeriod?.avgResolutionDays || 0 }} dni</span>
                          </div>
                        </div>
                      </div>

                      <!-- Still open -->
                      <div class="bg-amber-50 rounded-lg p-3">
                        <div class="flex items-center justify-between mb-2">
                          <span class="text-sm font-medium text-amber-700">Stale otevreno</span>
                          <span class="text-xl font-bold text-amber-600">{{ monthlyReport.lifecycle.stillOpen?.total || 0 }}</span>
                        </div>
                        <div class="text-xs text-gray-600 space-y-1">
                          <div class="flex justify-between">
                            <span>Nove (cekajici):</span>
                            <span class="font-medium">{{ monthlyReport.lifecycle.stillOpen?.createdInPeriod || 0 }}</span>
                          </div>
                          <div class="flex justify-between">
                            <span>Backlog (stare):</span>
                            <span class="font-medium">{{ monthlyReport.lifecycle.stillOpen?.createdBefore?.total || 0 }}</span>
                          </div>
                          <div v-if="monthlyReport.lifecycle.stillOpen?.createdBefore?.total > 0" class="pl-2 border-l-2 border-amber-200 mt-1">
                            <div class="flex justify-between text-gray-500">
                              <span>14+ dni:</span>
                              <span>{{ monthlyReport.lifecycle.stillOpen?.createdBefore?.over14days || 0 }}</span>
                            </div>
                            <div class="flex justify-between text-gray-500">
                              <span>30+ dni:</span>
                              <span>{{ monthlyReport.lifecycle.stillOpen?.createdBefore?.over30days || 0 }}</span>
                            </div>
                            <div class="flex justify-between text-orange-600">
                              <span>60+ dni:</span>
                              <span>{{ monthlyReport.lifecycle.stillOpen?.createdBefore?.over60days || 0 }}</span>
                            </div>
                            <div class="flex justify-between text-red-600 font-medium">
                              <span>90+ dni:</span>
                              <span>{{ monthlyReport.lifecycle.stillOpen?.createdBefore?.over90days || 0 }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Summary metrics -->
                    <div class="grid grid-cols-4 gap-2 text-center text-xs">
                      <div class="bg-blue-50 rounded p-2">
                        <div class="font-bold text-blue-600">{{ monthlyReport.lifecycle.newTickets || 0 }}</div>
                        <div class="text-gray-500">Novych</div>
                      </div>
                      <div class="bg-green-50 rounded p-2">
                        <div class="font-bold text-green-600">{{ monthlyReport.lifecycle.resolutionRate || 0 }}%</div>
                        <div class="text-gray-500">Vyreseno</div>
                      </div>
                      <div class="bg-purple-50 rounded p-2">
                        <div class="font-bold text-purple-600">{{ monthlyReport.lifecycle.backlogCleared || 0 }}</div>
                        <div class="text-gray-500">Backlog</div>
                      </div>
                      <div :class="monthlyReport.lifecycle.trend > 0 ? 'bg-red-50' : 'bg-green-50'" class="rounded p-2">
                        <div :class="monthlyReport.lifecycle.trend > 0 ? 'text-red-600' : 'text-green-600'" class="font-bold">
                          {{ monthlyReport.lifecycle.trend > 0 ? '+' : '' }}{{ monthlyReport.lifecycle.trend || 0 }}
                        </div>
                        <div class="text-gray-500">Trend</div>
                      </div>
                    </div>
                  </div>

                  <!-- Sentiment breakdown -->
                  <div v-if="monthlyReport.sentiment" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3">Sentiment zakazniku</h5>
                    <div class="grid grid-cols-5 gap-2">
                      <div class="text-center p-2 bg-green-50 rounded-lg">
                        <div class="text-lg font-bold text-green-600">{{ monthlyReport.sentiment.positive }}</div>
                        <div class="text-xs text-gray-500">Pozitivni</div>
                      </div>
                      <div class="text-center p-2 bg-gray-50 rounded-lg">
                        <div class="text-lg font-bold text-gray-600">{{ monthlyReport.sentiment.neutral }}</div>
                        <div class="text-xs text-gray-500">Neutralni</div>
                      </div>
                      <div class="text-center p-2 bg-red-50 rounded-lg">
                        <div class="text-lg font-bold text-red-600">{{ monthlyReport.sentiment.negative }}</div>
                        <div class="text-xs text-gray-500">Negativni</div>
                      </div>
                      <div class="text-center p-2 bg-orange-50 rounded-lg">
                        <div class="text-lg font-bold text-orange-600">{{ monthlyReport.sentiment.frustrated }}</div>
                        <div class="text-xs text-gray-500">Frustrovani</div>
                      </div>
                      <div class="text-center p-2 bg-purple-50 rounded-lg">
                        <div class="text-lg font-bold text-purple-600">{{ monthlyReport.sentiment.urgent }}</div>
                        <div class="text-xs text-gray-500">Urgentni</div>
                      </div>
                    </div>
                  </div>

                  <!-- Global Sentiment by Customer Type -->
                  <div v-if="monthlyReport.sentiment?.byCustomerType" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Sentiment podle typu zakaznika
                    </h5>
                    <div class="grid grid-cols-2 gap-4">
                      <div class="bg-blue-50 rounded-lg p-3">
                        <div class="text-sm font-medium text-blue-700 mb-2">Servisni technici</div>
                        <div class="flex items-center gap-2">
                          <span class="text-green-600 font-bold">{{ monthlyReport.sentiment.byCustomerType.technicians?.positiveRate || 0 }}%</span>
                          <span class="text-gray-400">poz.</span>
                          <span class="text-red-600 font-bold ml-2">{{ monthlyReport.sentiment.byCustomerType.technicians?.negativeRate || 0 }}%</span>
                          <span class="text-gray-400">neg.</span>
                        </div>
                        <div class="text-xs text-gray-500 mt-1">{{ monthlyReport.sentiment.byCustomerType.technicians?.total || 0 }} ticketu</div>
                      </div>
                      <div class="bg-purple-50 rounded-lg p-3">
                        <div class="text-sm font-medium text-purple-700 mb-2">Koncoví zakaznici</div>
                        <div class="flex items-center gap-2">
                          <span class="text-green-600 font-bold">{{ monthlyReport.sentiment.byCustomerType.endCustomers?.positiveRate || 0 }}%</span>
                          <span class="text-gray-400">poz.</span>
                          <span class="text-red-600 font-bold ml-2">{{ monthlyReport.sentiment.byCustomerType.endCustomers?.negativeRate || 0 }}%</span>
                          <span class="text-gray-400">neg.</span>
                        </div>
                        <div class="text-xs text-gray-500 mt-1">{{ monthlyReport.sentiment.byCustomerType.endCustomers?.total || 0 }} ticketu</div>
                      </div>
                    </div>
                    <!-- Frustration Sources -->
                    <div v-if="monthlyReport.sentiment.frustrationSources" class="mt-4 pt-3 border-t border-gray-100">
                      <div class="text-sm font-medium text-gray-600 mb-2">Zdroje frustrace:</div>
                      <div class="flex flex-wrap gap-2">
                        <span v-if="monthlyReport.sentiment.frustrationSources.porucha" class="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                          Porucha: {{ monthlyReport.sentiment.frustrationSources.porucha }}
                        </span>
                        <span v-if="monthlyReport.sentiment.frustrationSources.cekani" class="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">
                          Cekani: {{ monthlyReport.sentiment.frustrationSources.cekani }}
                        </span>
                        <span v-if="monthlyReport.sentiment.frustrationSources.komunikace" class="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                          Komunikace: {{ monthlyReport.sentiment.frustrationSources.komunikace }}
                        </span>
                        <span v-if="monthlyReport.sentiment.frustrationSources.reseni" class="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs">
                          Reseni: {{ monthlyReport.sentiment.frustrationSources.reseni }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Department Metrics -->
                  <div v-if="monthlyReport.departmentMetrics" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Metriky oddeleni
                    </h5>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div class="text-center p-2 bg-gray-50 rounded-lg">
                        <div class="text-lg font-bold text-gray-800">{{ monthlyReport.departmentMetrics.avgResponsesPerTicket || 0 }}</div>
                        <div class="text-xs text-gray-500">Odpovedi/ticket</div>
                      </div>
                      <div class="text-center p-2 bg-gray-50 rounded-lg">
                        <div class="text-lg font-bold text-blue-600">{{ monthlyReport.departmentMetrics.followUpRate || 0 }}%</div>
                        <div class="text-xs text-gray-500">Follow-up rate</div>
                      </div>
                      <div class="text-center p-2 bg-gray-50 rounded-lg">
                        <div class="text-lg font-bold" :class="monthlyReport.departmentMetrics.workloadDistribution?.status === 'nerovnomerne' ? 'text-amber-600' : 'text-green-600'">
                          {{ monthlyReport.departmentMetrics.workloadDistribution?.status || 'N/A' }}
                        </div>
                        <div class="text-xs text-gray-500">Rozlozeni zateze</div>
                      </div>
                      <div class="text-center p-2 bg-gray-50 rounded-lg">
                        <div class="text-lg font-bold text-gray-800">{{ monthlyReport.departmentMetrics.workloadDistribution?.avgTicketsPerAgent || 0 }}</div>
                        <div class="text-xs text-gray-500">Prumer/agent</div>
                      </div>
                    </div>
                    <!-- Problem Types -->
                    <div v-if="monthlyReport.departmentMetrics.problemTypeBreakdown?.length > 0" class="mt-3">
                      <div class="text-sm font-medium text-gray-600 mb-2">Typy problemu:</div>
                      <div class="flex flex-wrap gap-2">
                        <span v-for="pt in monthlyReport.departmentMetrics.problemTypeBreakdown.slice(0, 6)" :key="pt.type" class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          {{ pt.type }}: {{ pt.count }} ({{ pt.percentage }}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Agent Rankings -->
                  <div v-if="monthlyReport.agentRankings" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg class="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      Zebricek agentu
                    </h5>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div class="bg-green-50 rounded-lg p-2">
                        <div class="text-xs font-medium text-green-700 mb-1">Nejlepsi uzavreni</div>
                        <div v-for="a in (monthlyReport.agentRankings.bestClosureRate || []).slice(0, 2)" :key="a.name" class="text-sm">
                          {{ a.name }} <span class="text-green-600 font-bold">{{ a.value }}%</span>
                        </div>
                      </div>
                      <div class="bg-blue-50 rounded-lg p-2">
                        <div class="text-xs font-medium text-blue-700 mb-1">Nejlepsi sentiment</div>
                        <div v-for="a in (monthlyReport.agentRankings.bestSentiment || []).slice(0, 2)" :key="a.name" class="text-sm">
                          {{ a.name }} <span class="text-blue-600 font-bold">{{ a.value }}%</span>
                        </div>
                      </div>
                      <div class="bg-purple-50 rounded-lg p-2">
                        <div class="text-xs font-medium text-purple-700 mb-1">Nejuplnejsi reseni</div>
                        <div v-for="a in (monthlyReport.agentRankings.bestCompleteness || []).slice(0, 2)" :key="a.name" class="text-sm">
                          {{ a.name }} <span class="text-purple-600 font-bold">{{ a.value }}%</span>
                        </div>
                      </div>
                      <div class="bg-cyan-50 rounded-lg p-2">
                        <div class="text-xs font-medium text-cyan-700 mb-1">Nejproaktivnejsi</div>
                        <div v-for="a in (monthlyReport.agentRankings.mostProactive || []).slice(0, 2)" :key="a.name" class="text-sm">
                          {{ a.name }} <span class="text-cyan-600 font-bold">{{ a.value }}%</span>
                        </div>
                      </div>
                    </div>
                    <div v-if="monthlyReport.agentRankings.needsImprovement?.length > 0" class="mt-3 p-2 bg-amber-50 rounded-lg">
                      <div class="text-xs font-medium text-amber-700 mb-1">Potrebuje zlepseni:</div>
                      <div v-for="a in monthlyReport.agentRankings.needsImprovement" :key="a.name" class="text-sm text-amber-800">
                        {{ a.name }} - sentiment {{ a.positiveRate }}%, uzavreni {{ a.closureRate }}%
                      </div>
                    </div>
                  </div>

                  <!-- Detailed Agent Performance -->
                  <div v-if="monthlyReport.agents?.length > 0" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Detailni vykonnost agentu
                    </h5>
                    <div class="overflow-x-auto">
                      <table class="w-full text-sm">
                        <thead>
                          <tr class="text-left text-gray-500 border-b text-xs">
                            <th class="pb-2 pr-2">Agent</th>
                            <th class="pb-2 pr-2 text-center">Tickety</th>
                            <th class="pb-2 pr-2 text-center">Uzavreno</th>
                            <th class="pb-2 pr-2 text-center">Sentiment</th>
                            <th class="pb-2 pr-2 text-center">Uplnost</th>
                            <th class="pb-2 pr-2 text-center">Follow-up</th>
                            <th class="pb-2 text-center">vs Prumer</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="agent in monthlyReport.agents"
                            :key="agent.name"
                            class="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td class="py-2 pr-2 font-medium text-gray-800">{{ agent.name }}</td>
                            <td class="py-2 pr-2 text-center text-gray-600">{{ agent.ticketsHandled }}</td>
                            <td class="py-2 pr-2 text-center">
                              <span :class="agent.closureRate >= 70 ? 'text-green-600' : agent.closureRate >= 50 ? 'text-amber-600' : 'text-red-600'" class="font-medium">
                                {{ agent.closureRate }}%
                              </span>
                            </td>
                            <td class="py-2 pr-2 text-center">
                              <span :class="agent.positiveRate >= 50 ? 'text-green-600' : agent.positiveRate >= 30 ? 'text-amber-600' : 'text-red-600'" class="font-medium">
                                {{ agent.positiveRate }}%
                              </span>
                              <span class="text-xs text-gray-400 ml-1">(+{{ agent.sentiment?.positive || 0 }}/-{{ agent.sentiment?.negative || 0 }})</span>
                            </td>
                            <td class="py-2 pr-2 text-center">
                              <span :class="agent.completenessRate >= 60 ? 'text-green-600' : agent.completenessRate >= 40 ? 'text-amber-600' : 'text-red-600'" class="font-medium">
                                {{ agent.completenessRate }}%
                              </span>
                            </td>
                            <td class="py-2 pr-2 text-center text-blue-600">{{ agent.followUpRate }}%</td>
                            <td class="py-2 text-center">
                              <span :class="agent.vsAverage?.positiveRate >= 0 ? 'text-green-600' : 'text-red-600'" class="text-xs">
                                {{ agent.vsAverage?.positiveRate >= 0 ? '+' : '' }}{{ agent.vsAverage?.positiveRate || 0 }}%
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Positive References -->
                  <div v-if="monthlyReport.positiveReferences?.length > 0" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                      </svg>
                      Pozitivni reference ({{ monthlyReport.positiveReferences.length }})
                      <span class="text-xs text-gray-500 ml-auto">
                        {{ monthlyReport.positiveReferences.filter(r => !r.isTechnician).length }} od zakazniku,
                        {{ monthlyReport.positiveReferences.filter(r => r.isTechnician).length }} od techniku
                      </span>
                    </h5>
                    <div class="overflow-x-auto">
                      <table class="w-full text-sm">
                        <thead>
                          <tr class="text-left text-gray-500 border-b text-xs">
                            <th class="pb-2 pr-2">Ticket</th>
                            <th class="pb-2 pr-2">Popis</th>
                            <th class="pb-2 pr-2">Agent</th>
                            <th class="pb-2">Zdroj</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="ref in monthlyReport.positiveReferences"
                            :key="ref.ticketId"
                            class="border-b border-gray-50 hover:bg-gray-50"
                          >
                            <td class="py-1.5 pr-2">
                              <a
                                :href="`https://hotjet.freshdesk.com/a/tickets/${ref.ticketId}`"
                                target="_blank"
                                class="text-blue-600 hover:underline font-mono text-xs"
                              >
                                #{{ ref.ticketId }}
                              </a>
                            </td>
                            <td class="py-1.5 pr-2 text-gray-700 max-w-xs truncate" :title="ref.shortSummary">
                              {{ ref.shortSummary }}
                            </td>
                            <td class="py-1.5 pr-2 text-gray-600">{{ ref.agent }}</td>
                            <td class="py-1.5">
                              <span
                                :class="ref.isTechnician ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'"
                                class="px-1.5 py-0.5 rounded text-xs"
                              >
                                {{ ref.isTechnician ? 'Technik' : 'Zakaznik' }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p v-if="monthlyReport.positiveReferences.filter(r => r.isTechnician).length > monthlyReport.positiveReferences.filter(r => !r.isTechnician).length" class="text-xs text-amber-600 mt-2">
                      ⚠️ Vetsina pozitivnich hodnoceni pochazi od techniku, ne od koncovych zakazniku
                    </p>
                  </div>

                  <!-- AI Insights -->
                  <div v-if="monthlyReport.insights" class="bg-white rounded-lg p-4 shadow-sm">
                    <h5 class="font-medium text-gray-700 mb-4 flex items-center gap-2">
                      <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      AI Poznatky
                    </h5>
                    <div class="space-y-4 text-sm">
                      <!-- Top Issues with counts -->
                      <div v-if="monthlyReport.insights.topIssues?.length > 0">
                        <div class="font-medium text-red-600 mb-2">Nejcastejsi poruchy a problemy:</div>
                        <div class="space-y-2">
                          <div v-for="(issue, idx) in monthlyReport.insights.topIssues" :key="idx" class="bg-red-50 rounded p-2">
                            <div class="flex items-start gap-2">
                              <span class="bg-red-200 text-red-800 px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap">
                                {{ typeof issue === 'object' ? issue.count + 'x' : '' }}
                              </span>
                              <div>
                                <span class="font-medium text-gray-800">{{ typeof issue === 'object' ? issue.issue : issue }}</span>
                                <p v-if="issue.details" class="text-gray-600 text-xs mt-0.5">{{ issue.details }}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Solutions used -->
                      <div v-if="monthlyReport.insights.solutionsUsed?.length > 0">
                        <div class="font-medium text-blue-600 mb-2">Pouzita reseni:</div>
                        <div class="space-y-1">
                          <div v-for="(sol, idx) in monthlyReport.insights.solutionsUsed" :key="idx" class="flex items-center gap-2 bg-blue-50 rounded p-2">
                            <span class="bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs font-bold">{{ sol.count }}x</span>
                            <span class="text-gray-800">{{ sol.solution }}</span>
                            <span :class="sol.effectiveness === 'úspěšné' ? 'text-green-600' : sol.effectiveness === 'částečné' ? 'text-yellow-600' : 'text-red-600'" class="text-xs ml-auto">
                              {{ sol.effectiveness }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Frustration Analysis -->
                      <div v-if="monthlyReport.insights.frustrationAnalysis" class="bg-orange-50 rounded p-3">
                        <div class="font-medium text-orange-600 mb-2">
                          Analyza frustrace zakazniku
                          <span class="text-orange-500 font-normal">(celkem {{ monthlyReport.insights.frustrationAnalysis.total || monthlyReport.sentiment?.frustrated || '?' }})</span>
                        </div>
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <div class="text-xs text-gray-500 mb-1">Z poruchy:</div>
                            <div class="font-bold text-lg text-orange-700">{{ monthlyReport.insights.frustrationAnalysis.fromInitialProblem?.count || 0 }}</div>
                            <ul v-if="monthlyReport.insights.frustrationAnalysis.fromInitialProblem?.examples?.length" class="text-xs text-gray-600 mt-1 space-y-0.5">
                              <li v-for="ex in monthlyReport.insights.frustrationAnalysis.fromInitialProblem.examples.slice(0, 2)" :key="ex">• {{ ex }}</li>
                            </ul>
                          </div>
                          <div>
                            <div class="text-xs text-gray-500 mb-1">Z prubehu reseni:</div>
                            <div class="font-bold text-lg text-red-700">{{ monthlyReport.insights.frustrationAnalysis.fromResolutionProcess?.count || 0 }}</div>
                            <ul v-if="monthlyReport.insights.frustrationAnalysis.fromResolutionProcess?.examples?.length" class="text-xs text-gray-600 mt-1 space-y-0.5">
                              <li v-for="ex in monthlyReport.insights.frustrationAnalysis.fromResolutionProcess.examples.slice(0, 2)" :key="ex">• {{ ex }}</li>
                            </ul>
                          </div>
                          <div>
                            <div class="text-xs text-gray-500 mb-1">Z komunikace:</div>
                            <div class="font-bold text-lg text-yellow-700">{{ monthlyReport.insights.frustrationAnalysis.fromCommunication?.count || 0 }}</div>
                            <ul v-if="monthlyReport.insights.frustrationAnalysis.fromCommunication?.examples?.length" class="text-xs text-gray-600 mt-1 space-y-0.5">
                              <li v-for="ex in monthlyReport.insights.frustrationAnalysis.fromCommunication.examples.slice(0, 2)" :key="ex">• {{ ex }}</li>
                            </ul>
                          </div>
                          <div>
                            <div class="text-xs text-gray-500 mb-1">Ostatni:</div>
                            <div class="font-bold text-lg text-gray-600">{{ monthlyReport.insights.frustrationAnalysis.fromOther?.count || 0 }}</div>
                            <ul v-if="monthlyReport.insights.frustrationAnalysis.fromOther?.examples?.length" class="text-xs text-gray-600 mt-1 space-y-0.5">
                              <li v-for="ex in monthlyReport.insights.frustrationAnalysis.fromOther.examples.slice(0, 2)" :key="ex">• {{ ex }}</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <!-- Recurring Patterns -->
                      <div v-if="monthlyReport.insights.recurringPatterns?.length > 0">
                        <div class="font-medium text-purple-600 mb-2">Opakujici se vzorce:</div>
                        <ul class="list-disc list-inside text-gray-700 space-y-1">
                          <li v-for="pattern in monthlyReport.insights.recurringPatterns" :key="pattern">{{ pattern }}</li>
                        </ul>
                      </div>

                      <!-- Improvement Areas -->
                      <div v-if="monthlyReport.insights.improvementAreas?.length > 0">
                        <div class="font-medium text-amber-600 mb-2">Oblasti ke zlepseni:</div>
                        <ul class="list-disc list-inside text-gray-700 space-y-1">
                          <li v-for="area in monthlyReport.insights.improvementAreas" :key="area">{{ area }}</li>
                        </ul>
                      </div>

                      <!-- Positive Highlights -->
                      <div v-if="monthlyReport.insights.positiveHighlights?.length > 0">
                        <div class="font-medium text-green-600 mb-2">Pozitivni zjisteni:</div>
                        <ul class="list-disc list-inside text-gray-700 space-y-1">
                          <li v-for="h in monthlyReport.insights.positiveHighlights" :key="h">{{ h }}</li>
                        </ul>
                      </div>

                      <!-- AI Agent Insights -->
                      <div v-if="monthlyReport.insights.agentInsights?.length > 0">
                        <div class="font-medium text-indigo-600 mb-2 flex items-center gap-2">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Doporuceni pro jednotlive agenty:
                        </div>
                        <div class="space-y-2">
                          <div v-for="ai in monthlyReport.insights.agentInsights" :key="ai.agent" class="bg-indigo-50 rounded p-3">
                            <div class="font-medium text-indigo-700">{{ ai.agent }}</div>
                            <div v-if="ai.strengths?.length" class="mt-1">
                              <span class="text-xs text-green-600 font-medium">Silne stranky:</span>
                              <span class="text-xs text-gray-700"> {{ ai.strengths.join(', ') }}</span>
                            </div>
                            <div v-if="ai.improvements?.length" class="mt-1">
                              <span class="text-xs text-amber-600 font-medium">Ke zlepseni:</span>
                              <span class="text-xs text-gray-700"> {{ ai.improvements.join(', ') }}</span>
                            </div>
                            <div v-if="ai.recommendation" class="mt-1 text-xs text-indigo-600 italic">
                              {{ ai.recommendation }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Team Recommendations -->
                      <div v-if="monthlyReport.insights.teamRecommendations?.length > 0">
                        <div class="font-medium text-blue-600 mb-2 flex items-center gap-2">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          Doporuceni pro tym:
                        </div>
                        <div class="space-y-2">
                          <div v-for="rec in monthlyReport.insights.teamRecommendations" :key="rec.area" class="flex items-start gap-2 bg-blue-50 rounded p-2">
                            <span :class="rec.priority === 'vysoká' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'" class="px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap">
                              {{ rec.priority }}
                            </span>
                            <div>
                              <div class="font-medium text-gray-800">{{ rec.area }}: {{ rec.action }}</div>
                              <div v-if="rec.expectedImpact" class="text-xs text-gray-600">Ocekavany prinos: {{ rec.expectedImpact }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Training Needs -->
                      <div v-if="monthlyReport.insights.trainingNeeds?.length > 0">
                        <div class="font-medium text-cyan-600 mb-2">Potreby skoleni:</div>
                        <div class="flex flex-wrap gap-2">
                          <span v-for="t in monthlyReport.insights.trainingNeeds" :key="t" class="px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-xs">
                            {{ t }}
                          </span>
                        </div>
                      </div>

                      <!-- Summary Text -->
                      <div v-if="monthlyReport.insights.summaryText" class="bg-gray-50 rounded p-3 border-l-4 border-purple-500">
                        <p class="text-gray-700 italic">{{ monthlyReport.insights.summaryText }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Token usage footer -->
                  <div class="text-xs text-purple-500 text-right pt-2 border-t border-purple-100">
                    Tokeny: {{ formatNumber((monthlyReport.tokens?.input || 0) + (monthlyReport.tokens?.output || 0)) }} |
                    Cena: ${{ (monthlyReport.tokens?.estimatedCostUsd || 0).toFixed(3) }}
                  </div>
                </div>

                <!-- Empty state -->
                <div v-else class="text-center py-8 text-gray-500">
                  <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>Kliknete na "Generovat report" pro vytvoreni mesicniho AI reportu</p>
                  <p class="text-xs mt-2">Zpracuje vsechny tickety a vygeneruje komplexni analyzu</p>
                </div>
              </div>
              </div><!-- END MONTHLY TAB -->

              <!-- YEARLY TAB -->
              <div v-show="statsModalTab === 'yearly'" class="space-y-6">
              <!-- YEARLY REPORT -->
            <div class="border-t border-gray-200 mt-6 pt-6">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-green-600">Rocni AI report</h3>
                <div class="flex items-center gap-2">
                  <select v-model="yearlyReportYear" class="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                    <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
                  </select>
                  <button
                    @click="generateYearlyReport"
                    :disabled="yearlyReportLoading"
                    class="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm disabled:opacity-50"
                  >
                    {{ yearlyReportLoading ? 'Generuji...' : 'Generovat rocni report' }}
                  </button>
                  <button
                    v-if="yearlyReport"
                    @click="printYearlyReport"
                    class="px-2 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm"
                    title="Tisk do PDF"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                  </button>
                  <!-- Load saved yearly reports dropdown -->
                  <div class="relative">
                    <button
                      @click="toggleSavedYearlyReports"
                      class="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                      title="Nacist ulozeny rocni report"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Nacist
                    </button>
                    <div
                      v-if="showSavedYearlyReportsDropdown"
                      class="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto"
                    >
                      <div class="p-2 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                        <span class="text-sm font-medium text-gray-700">Dostupne roky</span>
                        <button @click="showSavedYearlyReportsDropdown = false" class="text-gray-400 hover:text-gray-600">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div v-if="loadingSavedYearlyReports" class="p-4 text-center text-gray-500 text-sm">
                        Nacitam...
                      </div>
                      <div v-else-if="savedYearlyReports.length === 0" class="p-4 text-center text-gray-500 text-sm">
                        Zadna data - nejprve vygenerujte mesicni reporty
                      </div>
                      <div v-else>
                        <button
                          v-for="report in savedYearlyReports"
                          :key="report.year"
                          @click="loadSavedYearlyReport(report.year)"
                          class="w-full px-3 py-2 text-left text-sm hover:bg-green-50 border-b border-gray-100 last:border-0"
                        >
                          <div class="font-medium text-gray-800">{{ report.label }}</div>
                          <div class="text-xs text-gray-500">
                            {{ report.totalTickets }} ticketu | ${{ report.estimatedCostUsd?.toFixed(2) || '0.00' }}
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Yearly report loading -->
              <div v-if="yearlyReportLoading" class="text-center py-8">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-green-600 border-t-transparent"></div>
                <p class="text-sm text-gray-500 mt-2">Agregovani rocniho reportu z ulozenych souhrnu...</p>
              </div>

              <!-- Yearly report content -->
              <div v-else-if="yearlyReport" class="space-y-4">
                <!-- Header -->
                <div class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <h4 class="text-xl font-bold text-green-700">{{ yearlyReport.period?.label || `Rok ${yearlyReportYear}` }}</h4>
                  <div class="grid grid-cols-4 gap-4 mt-3">
                    <div class="text-center">
                      <div class="text-2xl font-bold text-gray-800">{{ yearlyReport.volume?.totalTickets || 0 }}</div>
                      <div class="text-xs text-gray-500">Celkem ticketu</div>
                    </div>
                    <div class="text-center">
                      <div class="text-2xl font-bold text-green-600">{{ yearlyReport.volume?.closedTickets || 0 }}</div>
                      <div class="text-xs text-gray-500">Uzavreno</div>
                    </div>
                    <div class="text-center">
                      <div class="text-2xl font-bold text-blue-600">{{ yearlyReport.volume?.closureRate || 0 }}%</div>
                      <div class="text-xs text-gray-500">Uspesnost</div>
                    </div>
                    <div class="text-center">
                      <div class="text-2xl font-bold text-amber-600">{{ yearlyReport.volume?.openTickets || 0 }}</div>
                      <div class="text-xs text-gray-500">Otevrenych</div>
                    </div>
                  </div>
                </div>

                <!-- Monthly breakdown chart -->
                <div v-if="yearlyReport.monthlyBreakdown?.length > 0" class="bg-white rounded-lg p-4 shadow-sm">
                  <h5 class="font-semibold text-gray-700 mb-3">Mesicni prehled</h5>
                  <div class="grid grid-cols-12 gap-1 h-32">
                    <div v-for="month in yearlyReport.monthlyBreakdown" :key="month.month" class="flex flex-col items-center">
                      <div class="flex-1 w-full flex flex-col justify-end">
                        <div
                          class="bg-green-500 rounded-t w-full"
                          :style="{ height: `${(month.closedTickets / maxMonthlyTickets) * 100}%` }"
                          :title="`${month.closedTickets} uzavreno`"
                        ></div>
                        <div
                          class="bg-amber-400 w-full"
                          :style="{ height: `${(month.openTickets / maxMonthlyTickets) * 100}%` }"
                          :title="`${month.openTickets} otevrenych`"
                        ></div>
                      </div>
                      <div class="text-xs text-gray-500 mt-1">{{ month.name?.substring(0, 3) }}</div>
                      <div class="text-xs font-medium">{{ month.totalTickets }}</div>
                    </div>
                  </div>
                  <div class="flex gap-4 mt-2 text-xs text-gray-500">
                    <span class="flex items-center gap-1"><span class="w-3 h-3 bg-green-500 rounded"></span> Uzavreno</span>
                    <span class="flex items-center gap-1"><span class="w-3 h-3 bg-amber-400 rounded"></span> Otevrenych</span>
                  </div>
                </div>

                <!-- Sentiment overview -->
                <div v-if="yearlyReport.sentiment" class="bg-white rounded-lg p-4 shadow-sm">
                  <h5 class="font-semibold text-gray-700 mb-3">Rocni sentiment</h5>
                  <div class="grid grid-cols-5 gap-2 text-center">
                    <div class="bg-green-50 rounded p-2">
                      <div class="text-lg font-bold text-green-600">{{ yearlyReport.sentiment.positive }}</div>
                      <div class="text-xs text-gray-500">Pozitivni</div>
                    </div>
                    <div class="bg-gray-50 rounded p-2">
                      <div class="text-lg font-bold text-gray-600">{{ yearlyReport.sentiment.neutral }}</div>
                      <div class="text-xs text-gray-500">Neutralni</div>
                    </div>
                    <div class="bg-red-50 rounded p-2">
                      <div class="text-lg font-bold text-red-600">{{ yearlyReport.sentiment.negative }}</div>
                      <div class="text-xs text-gray-500">Negativni</div>
                    </div>
                    <div class="bg-orange-50 rounded p-2">
                      <div class="text-lg font-bold text-orange-600">{{ yearlyReport.sentiment.frustrated }}</div>
                      <div class="text-xs text-gray-500">Frustrovani</div>
                    </div>
                    <div class="bg-purple-50 rounded p-2">
                      <div class="text-lg font-bold text-purple-600">{{ yearlyReport.sentiment.urgent }}</div>
                      <div class="text-xs text-gray-500">Nalehave</div>
                    </div>
                  </div>
                </div>

                <!-- Top issues -->
                <div v-if="yearlyReport.topIssues?.length > 0" class="bg-white rounded-lg p-4 shadow-sm">
                  <h5 class="font-semibold text-gray-700 mb-3">Nejcastejsi problemy</h5>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="issue in yearlyReport.topIssues.slice(0, 12)"
                      :key="issue.pattern"
                      class="px-2 py-1 bg-red-50 text-red-700 rounded text-sm"
                    >
                      {{ issue.pattern }} ({{ issue.count }}x)
                    </span>
                  </div>
                </div>

                <!-- Agents -->
                <div v-if="yearlyReport.agents?.length > 0" class="bg-white rounded-lg p-4 shadow-sm">
                  <h5 class="font-semibold text-gray-700 mb-3">Vykon agentu</h5>
                  <div class="overflow-x-auto">
                    <table class="w-full text-sm">
                      <thead>
                        <tr class="text-left text-gray-500 border-b">
                          <th class="pb-2">Agent</th>
                          <th class="pb-2 text-center">Tickety</th>
                          <th class="pb-2 text-center">Uzavreno</th>
                          <th class="pb-2 text-center">Uspesnost</th>
                          <th class="pb-2 text-center">Prumer dni</th>
                          <th class="pb-2 text-center">Sentiment</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="agent in yearlyReport.agents.slice(0, 10)" :key="agent.name" class="border-b border-gray-100">
                          <td class="py-2 font-medium">{{ agent.name }}</td>
                          <td class="py-2 text-center">{{ agent.ticketsHandled }}</td>
                          <td class="py-2 text-center text-green-600">{{ agent.ticketsClosed }}</td>
                          <td class="py-2 text-center">{{ agent.closureRate }}%</td>
                          <td class="py-2 text-center">{{ agent.avgResolutionDays ?? '-' }}</td>
                          <td class="py-2 text-center">
                            <span class="text-green-500">+{{ agent.sentiment?.positive || 0 }}</span>
                            <span class="text-gray-400 mx-1">/</span>
                            <span class="text-red-500">-{{ agent.sentiment?.negative || 0 }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Customers breakdown -->
                <div v-if="yearlyReport.customers" class="bg-white rounded-lg p-4 shadow-sm">
                  <h5 class="font-semibold text-gray-700 mb-3">Zakaznici</h5>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="bg-blue-50 rounded p-3">
                      <div class="text-lg font-bold text-blue-600">{{ yearlyReport.customers.serviceTechnicians?.ticketCount || 0 }}</div>
                      <div class="text-sm text-gray-600">Tickety od servisnich techniku</div>
                      <div class="text-xs text-gray-400">{{ yearlyReport.customers.serviceTechnicians?.uniqueCount || 0 }} unikatnich</div>
                    </div>
                    <div class="bg-purple-50 rounded p-3">
                      <div class="text-lg font-bold text-purple-600">{{ yearlyReport.customers.endCustomers?.ticketCount || 0 }}</div>
                      <div class="text-sm text-gray-600">Tickety od koncovych zakazniku</div>
                      <div class="text-xs text-gray-400">{{ yearlyReport.customers.endCustomers?.uniqueCount || 0 }} unikatnich</div>
                    </div>
                  </div>
                </div>

                <!-- AI Insights -->
                <div v-if="yearlyReport.insights" class="bg-white rounded-lg p-4 shadow-sm">
                  <h5 class="font-semibold text-gray-700 mb-3">AI Rocni analyza</h5>

                  <!-- Year summary -->
                  <div v-if="yearlyReport.insights.yearSummary" class="bg-green-50 rounded p-3 mb-4">
                    <p class="text-gray-700">{{ yearlyReport.insights.yearSummary }}</p>
                  </div>

                  <!-- Trends -->
                  <div v-if="yearlyReport.insights.trends?.length > 0" class="mb-4">
                    <h6 class="font-medium text-gray-600 mb-2">Trendy</h6>
                    <div class="space-y-2">
                      <div v-for="(trend, idx) in yearlyReport.insights.trends" :key="idx" class="flex items-center gap-2 bg-gray-50 rounded p-2">
                        <span :class="{
                          'text-green-500': trend.direction === 'up',
                          'text-red-500': trend.direction === 'down',
                          'text-gray-500': trend.direction === 'stable'
                        }">
                          {{ trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→' }}
                        </span>
                        <span class="text-sm">{{ trend.trend }}</span>
                        <span :class="{
                          'bg-red-100 text-red-700': trend.impact === 'high',
                          'bg-yellow-100 text-yellow-700': trend.impact === 'medium',
                          'bg-green-100 text-green-700': trend.impact === 'low'
                        }" class="px-1 text-xs rounded">
                          {{ trend.impact }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Top issue categories -->
                  <div v-if="yearlyReport.insights.topIssueCategories?.length > 0" class="mb-4">
                    <h6 class="font-medium text-gray-600 mb-2">Hlavni kategorie problemu</h6>
                    <div class="space-y-2">
                      <div v-for="(cat, idx) in yearlyReport.insights.topIssueCategories" :key="idx" class="bg-red-50 rounded p-2">
                        <div class="flex justify-between items-center">
                          <span class="font-medium text-red-700">{{ cat.category }}</span>
                          <span class="text-sm text-red-600">{{ cat.count }}x - {{ cat.trend }}</span>
                        </div>
                        <p class="text-xs text-gray-600 mt-1">{{ cat.details }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Seasonal patterns -->
                  <div v-if="yearlyReport.insights.seasonalPatterns?.length > 0" class="mb-4">
                    <h6 class="font-medium text-gray-600 mb-2">Sezonni vzorce</h6>
                    <ul class="text-sm text-gray-600 space-y-1">
                      <li v-for="pattern in yearlyReport.insights.seasonalPatterns" :key="pattern">• {{ pattern }}</li>
                    </ul>
                  </div>

                  <!-- Recommendations -->
                  <div v-if="yearlyReport.insights.recommendations?.length > 0" class="mb-4">
                    <h6 class="font-medium text-gray-600 mb-2">Doporuceni</h6>
                    <div class="space-y-2">
                      <div v-for="(rec, idx) in yearlyReport.insights.recommendations" :key="idx" class="bg-blue-50 rounded p-2">
                        <div class="flex items-center gap-2">
                          <span :class="{
                            'bg-red-500': rec.priority === 'high',
                            'bg-yellow-500': rec.priority === 'medium',
                            'bg-green-500': rec.priority === 'low'
                          }" class="w-2 h-2 rounded-full"></span>
                          <span class="font-medium text-blue-700">{{ rec.area }}</span>
                        </div>
                        <p class="text-sm text-gray-600 mt-1">{{ rec.suggestion }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Positive highlights -->
                  <div v-if="yearlyReport.insights.positiveHighlights?.length > 0" class="mb-4">
                    <h6 class="font-medium text-gray-600 mb-2">Pozitiva</h6>
                    <ul class="text-sm text-green-600 space-y-1">
                      <li v-for="h in yearlyReport.insights.positiveHighlights" :key="h">✓ {{ h }}</li>
                    </ul>
                  </div>

                  <!-- Areas for improvement -->
                  <div v-if="yearlyReport.insights.areasForImprovement?.length > 0">
                    <h6 class="font-medium text-gray-600 mb-2">Oblasti ke zlepseni</h6>
                    <ul class="text-sm text-amber-600 space-y-1">
                      <li v-for="area in yearlyReport.insights.areasForImprovement" :key="area">⚠ {{ area }}</li>
                    </ul>
                  </div>
                </div>

                <!-- Token usage footer -->
                <div class="text-xs text-green-500 text-right pt-2 border-t border-green-100">
                  Tokeny: {{ formatNumber((yearlyReport.tokens?.input || 0) + (yearlyReport.tokens?.output || 0)) }} |
                  Cena: ${{ (yearlyReport.tokens?.estimatedCostUsd || 0).toFixed(3) }}
                </div>
              </div>

              <!-- Empty state for yearly -->
              <div v-else class="text-center py-6 text-gray-500">
                <svg class="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p class="text-sm">Rocni report agreguje data z ulozenych souhrnu ticketu</p>
                <p class="text-xs mt-1">Nejprve vygenerujte mesicni reporty pro ziskani dat</p>
              </div>
            </div>
              </div><!-- END YEARLY TAB -->
            </div><!-- END Content -->

            <!-- Footer -->
            <div class="p-4 border-t bg-gray-50 flex-shrink-0">
              <button
                @click="showStatsModal = false"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
              >
                Zavrit
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useApi } from '@/composables/useApi.js';
import { useResource } from '@/composables/useResource.js';
import DOMPurify from 'dompurify';

const { get, post, put, del } = useApi();
import TechniciansManager from './TechniciansManager.vue';
import TechnicianTasksList from './TechnicianTasksList.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'analysis-complete', 'openPlantsSearch']);

// Mapování stavu ticketu (2–5) na CSS třídy — sdíleno napříč šablonou.
const STATUS_CLASS = {
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-indigo-100 text-indigo-700',
  4: 'bg-green-100 text-green-700',
  5: 'bg-gray-100 text-gray-600',
};

const isLoading = ref(false);
const loadingStatus = ref('');
const loadingProgress = ref(0);
const loadingTicketsCount = ref(0);
const loadingTitle = ref('Stahuji tickety z Freshdesku...');
const loadingDetail = ref('');
const error = ref(null);
const analysisData = ref(null);
const lastFetched = ref(null);
const selectedFilter = ref('all');
const selectedTicket = ref(null);
const ticketConversations = ref([]);
const loadingConversations = ref(false);
const quickFilter = ref(null);
const showFilterPanel = ref(false);
const agentsList = ref([]);
const aiSummary = ref(null);
const aiTokenUsage = ref(null);
const aiMetrics = ref(null);
const loadingAiSummary = ref(false);
const aiSummaryError = ref(null);
const savingNote = ref(false);
const showWhatsappFormat = ref(false);
const whatsappCopied = ref(false);
const noteSaved = ref(false);
const loadingAgents = ref(false);
const existingSummaryDate = ref(null);
const hasNewCommunication = ref(false);

// Customer popup
const showCustomerPopup = ref(false);
const selectedCustomer = ref(null);
const customerTickets = ref([]);
const loadingCustomerTickets = ref(false);

// Task assignment
const showTaskAssignment = ref(false);
const techniciansForAssignment = ref([]);
const taskAssignmentData = ref({
  technicianId: '',
  description: '',
  priority: 'normal',
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  customerAddress: ''
});
const savingTask = ref(false);
const taskSaved = ref(false);

// AI request generation for task creation
const generatingTaskRequestForCreation = ref(false);
const generatedTaskRequestForCreation = ref(null);
const taskRequestErrorForCreation = ref(null);

// Tab navigation
const activeTab = ref('tickets');
const tasksData = ref([]);
const tasksListRef = ref(null);
const techniciansRef = ref(null);

// Sorting
const sortBy = ref('activity'); // 'activity' | 'newest' | 'customer' | 'category'
const sortDirection = ref('desc'); // 'asc' | 'desc'

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(50);

// Header filter (client-side filtering of current data)
const headerFilter = ref(null); // 'new_today' | 'updated_today' | 'closed_today' | null

// Stats modal
const showStatsModal = ref(false);
const statsModalTab = ref('overview'); // 'overview' | 'daily' | 'monthly' | 'yearly'
const showDailyReport = ref(true); // Default open in modal
// Denní statistiky — jednotné načítání přes useResource (throw při !success → data zachována).
const { data: dailyStats, loading: loadingDailyStats, load: loadDailyStats } =
  useResource(async () => {
    const data = await get('/api/freshdesk/daily-stats');
    if (!data.success) throw new Error('daily-stats: neúspěšná odpověď');
    return data;
  }, { initial: null });

// Monthly report
const monthlyReportPeriod = ref('last7days');
const generatingMonthlyReport = ref(false);
const monthlyReport = ref(null);
const monthlyReportProgress = ref({
  status: 'idle',
  progress: 0,
  currentStep: 'Cekam...',
  currentMonth: '',
  currentTicketId: null,
  processedTickets: 0,
  totalTickets: 0,
  tokens: { input: 0, output: 0 },
  estimatedCostUsd: 0
});

// Daily report
// Default to yesterday's date
const getYesterdayDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};
const dailyReportDate = ref(getYesterdayDate());
const generatingDailyReport = ref(false);
const dailyReport = ref(null);
const dailyReportCached = ref(false);
const dailyReportChanges = ref(null); // { hasChanges, changes: { newTickets, summary } }
const checkingDailyReportChanges = ref(false);
const savedDailyReports = ref([]);
const loadingSavedDailyReports = ref(false);
const showSavedDailyReportsDropdown = ref(false);

// Recent days for daily report dropdown (last 8 days including today)
const recentDays = computed(() => {
  const days = [];
  const dayNames = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];
  for (let i = 0; i <= 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    let label = `${dayNames[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.`;
    if (i === 0) label = `Dnes - ${label}`;
    else if (i === 1) label = `Včera - ${label}`;
    if (isWeekend) label += ' (víkend)';
    days.push({
      value: d.toISOString().split('T')[0],
      label
    });
  }
  return days;
});

// Yearly batch generation (all months)
const generatingYearlyBatch = ref(false);
const yearlyBatchProgress = ref({
  status: 'idle',
  currentMonthIndex: 0,
  totalMonths: 0,
  currentMonth: '',
  monthsProcessed: [],
  monthsToProcess: [],
  overallProgress: 0,
  currentMonthProgress: 0,
  currentTicketId: null,
  processedTickets: 0,
  totalTickets: 0,
  tokens: { input: 0, output: 0 },
  estimatedCostUsd: 0
});

// Yearly report
const currentYear = new Date().getFullYear();
const availableYears = [currentYear, currentYear - 1, currentYear - 2];
const yearlyReportYear = ref(currentYear);
const yearlyReportLoading = ref(false);
const yearlyReport = ref(null);

// Saved reports (monthly)
const savedReports = ref([]);
const loadingSavedReports = ref(false);
const showSavedReportsDropdown = ref(false);

// Saved yearly reports
const savedYearlyReports = ref([]);
const loadingSavedYearlyReports = ref(false);
const showSavedYearlyReportsDropdown = ref(false);

// Computed for yearly chart
const maxMonthlyTickets = computed(() => {
  if (!yearlyReport.value?.monthlyBreakdown?.length) return 1;
  return Math.max(...yearlyReport.value.monthlyBreakdown.map(m => m.totalTickets)) || 1;
});

// Available months for monthly report (last 12 months)
const monthNames = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
                    'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];
const availableMonths = computed(() => {
  const months = [];
  const now = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `month_${d.getFullYear()}_${d.getMonth() + 1}`,
      label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`
    });
  }
  return months;
});

// Helper: normalize text for search (remove diacritics, lowercase)
function normalizeForSearch(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics
}

// Inline filter (above ticket list)
const searchQuery = ref('');
const inlineStatus = ref('all');
const inlineAgent = ref('all');

// AI Summary search (in filter panel)
const summarySearchQuery = ref('');
const summarySearchResults = ref([]);
const isSearchingSummaries = ref(false);
const hasSearched = ref(false);
const searchError = ref('');

// OZW search for ticket requester

// Filter settings - default: yesterday+today, unresolved, all agents
const filters = ref({
  dateRange: 'yesterday_today',
  customDate: '',
  status: 'unresolved',
  agent: 'all'
});

// Show all tickets when agent filter is active or no unresolved tickets
const showAllTickets = computed(() => {
  if (!analysisData.value) return false;
  // Show all tickets when specific agent is selected
  if (filters.value.agent !== 'all' && filters.value.agent !== 'unassigned') return true;
  // Or when there are no unresolved but there are other tickets
  if (analysisData.value.oldestUnresolved?.length === 0 && analysisData.value.allTickets?.length > 0) return true;
  return false;
});

// Filtered and sorted tickets (before pagination)
const filteredTickets = computed(() => {
  if (!analysisData.value) return [];

  let tickets;
  if (showAllTickets.value) {
    tickets = [...(analysisData.value.allTickets || [])];
  } else {
    tickets = [...(analysisData.value.oldestUnresolved || [])];
  }

  // Apply header filter (client-side)
  if (headerFilter.value) {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    switch (headerFilter.value) {
      case 'new_today':
        tickets = tickets.filter(t => new Date(t.created_at) >= todayStart);
        break;
      case 'updated_today':
        tickets = tickets.filter(t => {
          const created = new Date(t.created_at);
          return created < todayStart && t.hasConversations;
        });
        break;
      case 'closed_today':
        tickets = tickets.filter(t => t.status === 5);
        break;
    }
  }

  // Apply quickFilter for due_today (client-side filtering by due_by date)
  if (quickFilter.value === 'due_today') {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    tickets = tickets.filter(t => {
      if (!t.due_by) return false;
      const dueDate = new Date(t.due_by).toISOString().split('T')[0];
      return dueDate === todayStr;
    });
  }

  // Apply inline filters (fulltext search with diacritics normalization)
  if (searchQuery.value) {
    const query = normalizeForSearch(searchQuery.value);
    tickets = tickets.filter(t =>
      String(t.id).includes(query) ||
      normalizeForSearch(t.subject).includes(query) ||
      normalizeForSearch(t.requester).includes(query) ||
      normalizeForSearch(t.requester_email).includes(query) ||
      normalizeForSearch(t.description).includes(query)
    );
  }

  if (inlineStatus.value !== 'all') {
    const statusMap = { open: 2, pending: 3, resolved: 4, closed: 5 };
    tickets = tickets.filter(t => t.status === statusMap[inlineStatus.value]);
  }

  if (inlineAgent.value !== 'all') {
    if (inlineAgent.value === 'unassigned') {
      tickets = tickets.filter(t => !t.responder_id);
    } else {
      const agentId = parseInt(inlineAgent.value);
      tickets = tickets.filter(t => t.responder_id === agentId);
    }
  }

  // Sort tickets
  const dir = sortDirection.value === 'asc' ? 1 : -1;

  switch (sortBy.value) {
    case 'activity':
      tickets.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dir * (dateB - dateA);
      });
      break;
    case 'newest':
      tickets.sort((a, b) => dir * (b.id - a.id));
      break;
    case 'subject':
      tickets.sort((a, b) => {
        const subA = (a.subject || '').toLowerCase();
        const subB = (b.subject || '').toLowerCase();
        return dir * subA.localeCompare(subB, 'cs');
      });
      break;
    case 'status':
      tickets.sort((a, b) => dir * ((a.status || 0) - (b.status || 0)));
      break;
    case 'customer':
      tickets.sort((a, b) => {
        const nameA = (a.requester || '').toLowerCase();
        const nameB = (b.requester || '').toLowerCase();
        return dir * nameA.localeCompare(nameB, 'cs');
      });
      break;
    case 'agent':
      tickets.sort((a, b) => {
        const agentA = (a.agent || '').toLowerCase();
        const agentB = (b.agent || '').toLowerCase();
        return dir * agentA.localeCompare(agentB, 'cs');
      });
      break;
    case 'category':
      tickets.sort((a, b) => {
        const levelA = a.category?.level || 99;
        const levelB = b.category?.level || 99;
        return dir * (levelA - levelB);
      });
      break;
    case 'updated':
      tickets.sort((a, b) => {
        const dateA = new Date(a.updated_at || 0);
        const dateB = new Date(b.updated_at || 0);
        return dir * (dateB - dateA);
      });
      break;
    case 'age':
      tickets.sort((a, b) => dir * ((b.age || 0) - (a.age || 0)));
      break;
  }

  return tickets;
});

// Paginated tickets for display
const ticketsToShow = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredTickets.value.slice(start, end);
});

// Total pages
const totalPages = computed(() => {
  return Math.ceil(filteredTickets.value.length / itemsPerPage.value) || 1;
});

// Task stats for tab badge
const taskStats = computed(() => {
  const counts = { pending: 0, sent: 0, in_progress: 0, total: 0 };
  tasksData.value.forEach(t => {
    if (t.status === 'pending') counts.pending++;
    else if (t.status === 'sent') counts.sent++;
    else if (t.status === 'in_progress') counts.in_progress++;
    counts.total++;
  });
  return counts;
});

// Reset to page 1 when filter changes
watch([headerFilter, sortBy, sortDirection, searchQuery, inlineStatus, inlineAgent], () => {
  currentPage.value = 1;
});

// Toggle header filter (client-side)
function toggleHeaderFilter(filter) {
  if (headerFilter.value === filter) {
    headerFilter.value = null; // Deselect
  } else {
    headerFilter.value = filter;
  }
}

// Toggle sort by clicking column header
function toggleSort(column) {
  if (sortBy.value === column) {
    sortDirection.value = sortDirection.value === 'desc' ? 'asc' : 'desc';
  } else {
    sortBy.value = column;
    sortDirection.value = 'desc';
  }
}

// Conversation stats for selected ticket
const conversationStats = computed(() => {
  const convs = ticketConversations.value || [];
  const inbound = convs.filter(c => c.type === 'reply_customer').length;
  const outbound = convs.filter(c => c.type === 'reply_agent').length;
  const notes = convs.filter(c => c.type === 'note').length;
  // +1 for initial ticket description
  return {
    total: convs.length + 1,
    inbound: inbound + 1, // +1 for initial ticket
    outbound,
    notes
  };
});

// WhatsApp message for technician
const whatsappMessage = computed(() => {
  if (!aiSummary.value || !selectedTicket.value) return '';

  const ticket = selectedTicket.value;
  const summary = aiSummary.value;

  let msg = `🔧 *Ticket #${ticket.id}*\n\n`;

  // What customer needs
  if (summary.request) {
    msg += `📋 *Požadavek:*\n${summary.request}\n\n`;
  }

  // What happened / current state
  if (summary.communication) {
    msg += `💬 *Stav:*\n${summary.communication}\n\n`;
  }

  // What needs to be done
  if (summary.resolution) {
    const resolutionText = summary.resolutionMissing
      ? '⚠️ ' + summary.resolution
      : summary.resolution;
    msg += `✅ *Řešení/Akce:*\n${resolutionText}\n`;
  }

  return msg.trim();
});

function copyWhatsappMessage(withContact) {
  let text = whatsappMessage.value;

  if (withContact && selectedTicket.value) {
    const ticket = selectedTicket.value;
    text += '\n\n---\n📞 *Kontakt:*\n';
    if (ticket.requester) text += ticket.requester + '\n';
    if (ticket.requesterPhone) text += '📱 ' + ticket.requesterPhone + '\n';
    if (ticket.requesterEmail || ticket.requester_email) text += '✉️ ' + (ticket.requesterEmail || ticket.requester_email) + '\n';
    if (ticket.requesterAddress) text += '📍 ' + ticket.requesterAddress;
  }

  navigator.clipboard.writeText(text.trim());
  whatsappCopied.value = true;
  setTimeout(() => {
    whatsappCopied.value = false;
  }, 2000);
}

// Format date in local timezone (avoids UTC conversion issues)
function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Calculate date from filter
function getDateFromFilter() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filters.value.dateRange) {
    case 'today':
      return formatLocalDate(today);
    case 'yesterday_today': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return formatLocalDate(yesterday);
    }
    case 'days7': {
      const date = new Date(today);
      date.setDate(date.getDate() - 7);
      return formatLocalDate(date);
    }
    case 'days14': {
      const date = new Date(today);
      date.setDate(date.getDate() - 14);
      return formatLocalDate(date);
    }
    case 'days30': {
      const date = new Date(today);
      date.setDate(date.getDate() - 30);
      return formatLocalDate(date);
    }
    case 'days90': {
      const date = new Date(today);
      date.setDate(date.getDate() - 90);
      return formatLocalDate(date);
    }
    case 'year':
      return `${now.getFullYear()}-01-01`;
    case 'custom':
      return filters.value.customDate || '2025-01-01';
    default:
      return '2025-01-01';
  }
}

// Quick filter presets
function setQuickFilter(preset) {
  quickFilter.value = quickFilter.value === preset ? null : preset;

  switch (preset) {
    case 'new_today':
      // Nový = dnes + nepřiřazený
      filters.value.dateRange = 'today';
      filters.value.status = 'new';
      filters.value.agent = 'unassigned';
      break;
    case 'new_week':
      // Nové za týden = vytvořeno za týden + nepřiřazeno
      filters.value.dateRange = 'days7';
      filters.value.status = 'new';
      filters.value.agent = 'unassigned';
      break;
    case 'updated_today':
      filters.value.dateRange = 'yesterday_today';
      filters.value.status = 'all';
      filters.value.agent = 'all';
      break;
    case 'closed_today':
      filters.value.dateRange = 'yesterday_today';
      filters.value.status = 'closed';
      filters.value.agent = 'all';
      break;
    case 'unassigned':
      // Nepřiřazený = starší než dnes + nepřiřazený
      filters.value.dateRange = 'days30';
      filters.value.status = 'unresolved';
      filters.value.agent = 'unassigned';
      break;
    case 'urgent':
      filters.value.dateRange = 'days30';
      filters.value.status = 'open';
      filters.value.agent = 'all';
      break;
    case 'old_open':
      filters.value.dateRange = 'days90';
      filters.value.status = 'unresolved';
      filters.value.agent = 'all';
      break;
    case 'due_today':
      // Tickety s termínem dnes - stáhni všechny nevyřešené za 90 dní a pak filtruj podle due_by
      filters.value.dateRange = 'days90';
      filters.value.status = 'unresolved';
      filters.value.agent = 'all';
      break;
  }
}

// Apply quick filter and immediately fetch tickets
async function applyQuickFilter(preset) {
  setQuickFilter(preset);
  await fetchTickets();
}

// Watch for filter changes - don't auto-reload, just update selectedFilter
watch(() => filters.value.status, (newStatus) => {
  selectedFilter.value = newStatus;
});

// Load agents when modal opens
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    if (agentsList.value.length === 0) {
      loadAgents();
    }
    loadTasks();
  }
}, { immediate: true });

async function loadAgents() {
  if (loadingAgents.value) return;
  loadingAgents.value = true;

  try {
    const data = await get('/api/freshdesk/agents');
    if (data.success && data.agents) {
      agentsList.value = data.agents;
    }
  } catch (err) {
    console.error('Failed to load agents:', err);
  } finally {
    loadingAgents.value = false;
  }
}

async function loadTasks() {
  try {
    const data = await get('/api/technician-tasks/list');
    tasksData.value = data.tasks || [];
  } catch (err) {
    console.error('Failed to load tasks:', err);
  }
}

function close() {
  emit('close');
}

// Search in AI summaries (fulltext in Neon)
async function searchInSummaries() {
  if (!summarySearchQuery.value || summarySearchQuery.value.length < 2) return;

  isSearchingSummaries.value = true;
  hasSearched.value = true;
  summarySearchResults.value = [];
  searchError.value = '';

  try {
    const data = await get('/api/freshdesk/search-summaries', {
      query: summarySearchQuery.value,
      limit: 50
    });
    summarySearchResults.value = data.tickets || [];
  } catch (err) {
    console.error('Error searching summaries:', err);
    searchError.value = err.message || 'Chyba pri hledani';
    summarySearchResults.value = [];
  } finally {
    isSearchingSummaries.value = false;
  }
}


// Open OZW search view with customer name pre-filled
function openOzwSearchWithName() {
  if (!selectedTicket.value) return;
  const freshdeskContext = {
    customerName: selectedTicket.value.requester || '',
    customerEmail: selectedTicket.value.requester_email || '',
    ticketId: selectedTicket.value.id,
    ticketSubject: selectedTicket.value.subject || ''
  };
  emit('openPlantsSearch', freshdeskContext);
  emit('close'); // Close Freshdesk to show PlantsSearchView
}

// Load a single ticket from search results
async function loadSearchedTicket(ticketId) {
  showFilterPanel.value = false;
  await openTicketDetail({ id: ticketId });
}

// Load all tickets from search results
async function loadAllSearchedTickets() {
  if (summarySearchResults.value.length === 0) return;

  const ticketIds = summarySearchResults.value.map(r => r.ticketId);

  // Set a special filter mode for searched tickets
  isLoading.value = true;
  loadingProgress.value = 0;
  loadingStatus.value = 'Nacitam nalezene tickety...';
  showFilterPanel.value = false;

  try {
    // Fetch each ticket from Freshdesk
    const tickets = [];
    for (let i = 0; i < ticketIds.length; i++) {
      loadingProgress.value = Math.round((i / ticketIds.length) * 100);
      loadingStatus.value = `Nacitam ticket ${i + 1} z ${ticketIds.length}`;

      try {
        const data = await get('/api/freshdesk/tickets', { ticketId: ticketIds[i] });
        if (data.ticket) {
          // Map to expected format
          tickets.push({
            id: data.ticket.id,
            subject: data.ticket.subject,
            status: data.ticket.status,
            priority: data.ticket.priority,
            requester: data.ticket.requesterName,
            requester_email: data.ticket.requesterEmail,
            agent: data.ticket.agent || 'Neprirazeno',
            created_at: data.ticket.createdAt,
            updated_at: data.ticket.updatedAt,
            description: data.ticket.description,
            hasAiSummary: true // We know these have summaries
          });
        }
      } catch (e) {
        console.error(`Failed to fetch ticket ${ticketIds[i]}:`, e);
      }

      // Small delay to avoid rate limiting
      if (i < ticketIds.length - 1) {
        await new Promise(r => setTimeout(r, 200));
      }
    }

    // Update analysis data with searched tickets
    analysisData.value = {
      total: tickets.length,
      allTickets: tickets,
      oldestUnresolved: tickets.filter(t => [2, 3].includes(t.status)),
      byStatus: {},
      byPriority: {},
      todayStats: { newToday: 0, updatedToday: 0, closedToday: 0 }
    };

    lastFetched.value = new Date();
    loadingProgress.value = 100;
    loadingStatus.value = 'Hotovo';

  } catch (err) {
    console.error('Error loading searched tickets:', err);
    error.value = err.message;
  } finally {
    isLoading.value = false;
  }
}

async function fetchTickets() {
  isLoading.value = true;
  loadingProgress.value = 0;
  loadingTicketsCount.value = 0;
  loadingTitle.value = 'Stahuji tickety z Freshdesku...';
  loadingStatus.value = 'Pripojuji se k API';
  loadingDetail.value = '';
  error.value = null;
  showFilterPanel.value = false;
  headerFilter.value = null; // Reset header filter on new fetch

  try {
    loadingProgress.value = 10;
    loadingStatus.value = 'Nacitam tickety';
    loadingDetail.value = 'Cekam na odpoved serveru...';

    const fromDate = getDateFromFilter();
    const status = filters.value.status;
    const agent = filters.value.agent;

    const data = await get('/api/freshdesk/tickets', { filter: status, fromDate, agent });

    loadingProgress.value = 50;
    loadingStatus.value = 'Zpracovavam data';

    if (!data.success) {
      throw new Error(data.error || 'Neznama chyba');
    }

    loadingProgress.value = 70;
    loadingTicketsCount.value = data.analysis?.tickets?.length || 0;
    loadingStatus.value = 'Kontroluji AI souhrny';
    loadingDetail.value = `Nalezeno ${loadingTicketsCount.value} ticketu`;

    // Fetch saved summary IDs and mark tickets
    try {
      const summaryData = await get('/api/freshdesk/saved-summary-ids');
      const savedIds = new Set(summaryData.ticketIds || []);
      // Mark tickets with saved summaries
      if (data.analysis?.tickets) {
        data.analysis.tickets.forEach(ticket => {
          ticket.hasSavedSummary = savedIds.has(ticket.id);
        });
      }
    } catch (e) {
      console.warn('Could not load saved summary IDs:', e);
    }

    loadingProgress.value = 90;
    loadingStatus.value = 'Dokoncuji';
    loadingDetail.value = `Zpracovano ${loadingTicketsCount.value} ticketu`;

    analysisData.value = data.analysis;
    lastFetched.value = data.fetchedAt;
    // Don't overwrite agentsList if already loaded from /api/freshdesk/agents
    // (which has the combined agent+tag entries)
    if (data.agents && agentsList.value.length === 0) {
      agentsList.value = data.agents;
    }

    loadingProgress.value = 100;
    loadingStatus.value = 'Hotovo';
    loadingDetail.value = `Nacteno ${loadingTicketsCount.value} ticketu`;

    // Small delay to show 100%
    await new Promise(resolve => setTimeout(resolve, 300));

    emit('analysis-complete', data.analysis);
  } catch (err) {
    error.value = err.message || 'Chyba pri nacitani ticketu';
    console.error('Freshdesk fetch error:', err);
  } finally {
    isLoading.value = false;
    loadingStatus.value = '';
    loadingProgress.value = 0;
    loadingTicketsCount.value = 0;
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('cs-CZ');
}


function openTicketExternal(ticketId) {
  window.open(`https://hotjet.freshdesk.com/a/tickets/${ticketId}`, '_blank');
}

async function openCustomerTickets(requester, requesterEmail) {
  selectedCustomer.value = { name: requester, email: requesterEmail };
  showCustomerPopup.value = true;
  customerTickets.value = [];
  loadingCustomerTickets.value = true;

  try {
    // Fetch ALL tickets for this customer from API (using 1 year range)
    try {
      const data = await get('/api/freshdesk/tickets', { filter: 'all', fromDate: '2024-01-01', requesterEmail: requesterEmail || '' });
      if (data.success && data.analysis) {
        customerTickets.value = data.analysis.allTickets || [];
      }
    } catch {
      // Fallback to local filtering if API fails
      const allTickets = analysisData.value?.allTickets || [];
      customerTickets.value = allTickets.filter(t =>
        t.requester_email === requesterEmail || t.requester === requester
      );
    }
  } catch (err) {
    console.error('Failed to fetch customer tickets:', err);
    // Fallback to local filtering
    const allTickets = analysisData.value?.allTickets || [];
    customerTickets.value = allTickets.filter(t =>
      t.requester_email === requesterEmail || t.requester === requester
    );
  } finally {
    loadingCustomerTickets.value = false;
  }
}

function closeCustomerPopup() {
  showCustomerPopup.value = false;
  selectedCustomer.value = null;
  customerTickets.value = [];
}

// Task detail view
const selectedTask = ref(null);
const showTaskDetail = ref(false);
const completingTask = ref(false);
const taskCompletionData = ref({
  notes: '',
  result: 'completed',
  photos: []
});

// Task request generation
const generatingTaskRequest = ref(false);
const generatedTaskRequest = ref(null);
const taskRequestError = ref(null);

function handleTaskSelected(task) {
  selectedTask.value = task;
  showTaskDetail.value = true;
  taskCompletionData.value = {
    notes: '',
    result: 'completed',
    photos: []
  };
  // Load saved AI request if exists, otherwise reset
  generatedTaskRequest.value = task.ai_generated_request || null;
  taskRequestError.value = null;
}

function closeTaskDetail() {
  showTaskDetail.value = false;
  selectedTask.value = null;
}

async function generateTaskRequest() {
  if (!selectedTask.value) return;

  generatingTaskRequest.value = true;
  taskRequestError.value = null;
  generatedTaskRequest.value = null;

  try {
    // Find technician info
    const technician = techniciansForAssignment.value?.find(t => t.id === selectedTask.value.technician_id);

    const data = await post('/api/technician-tasks/generate-request', {
      task: selectedTask.value,
      ticket: selectedTicket.value,
      conversations: ticketConversations.value,
      aiSummary: aiSummary.value,
      technician: technician
    });

    if (data.success) {
      generatedTaskRequest.value = data.request;
      // Save the generated request to the task
      await saveAiGeneratedRequest(selectedTask.value.id, data.request);
    } else {
      throw new Error(data.error || 'Chyba pri generovani');
    }
  } catch (err) {
    taskRequestError.value = err.message;
    console.error('Error generating task request:', err);
  } finally {
    generatingTaskRequest.value = false;
  }
}

async function saveAiGeneratedRequest(taskId, request) {
  try {
    await put(`/api/technician-tasks/update?id=${taskId}`, { aiGeneratedRequest: request });
  } catch (err) {
    console.error('Error saving AI request to task:', err);
  }
}

function copyTaskRequest() {
  if (generatedTaskRequest.value) {
    navigator.clipboard.writeText(generatedTaskRequest.value);
    alert('Zkopirováno do schránky!');
  }
}

function sendTaskRequestEmail() {
  if (!generatedTaskRequest.value || !selectedTask.value) return;

  const technician = techniciansForAssignment.value?.find(t => t.id === selectedTask.value.technician_id);
  const email = technician?.email || '';
  const subject = encodeURIComponent(`Servisní požadavek #${selectedTask.value.ticket_id}`);
  const body = encodeURIComponent(generatedTaskRequest.value);

  window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
}

async function completeTask() {
  if (!selectedTask.value) return;

  completingTask.value = true;
  try {
    await post('/api/technician-tasks/complete', {
      taskId: selectedTask.value.id,
      notes: taskCompletionData.value.notes,
      result: taskCompletionData.value.result,
      photos: taskCompletionData.value.photos
    });

    closeTaskDetail();
    loadTasks();
    if (tasksListRef.value?.refresh) {
      tasksListRef.value.refresh();
    }
  } catch (err) {
    console.error('Error completing task:', err);
  } finally {
    completingTask.value = false;
  }
}

// Task assignment functions
async function openTaskAssignment() {
  showTaskAssignment.value = true;
  taskSaved.value = false;

  // Pre-fill customer data from ticket
  if (selectedTicket.value) {
    taskAssignmentData.value.customerName = selectedTicket.value.requester || '';
    taskAssignmentData.value.customerEmail = selectedTicket.value.requester_email || '';
  }

  // Load technicians
  try {
    const data = await get('/api/technicians/list');
    techniciansForAssignment.value = data.technicians || [];
  } catch (err) {
    console.error('Error loading technicians:', err);
  }
}

function closeTaskAssignment() {
  showTaskAssignment.value = false;
  taskAssignmentData.value = {
    technicianId: '',
    description: '',
    priority: 'normal',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: ''
  };
  // Reset AI request state
  generatedTaskRequestForCreation.value = null;
  taskRequestErrorForCreation.value = null;
}

async function generateTaskRequestForCreation() {
  if (!selectedTicket.value || !taskAssignmentData.value.technicianId) return;

  generatingTaskRequestForCreation.value = true;
  taskRequestErrorForCreation.value = null;
  generatedTaskRequestForCreation.value = null;

  try {
    // Find technician info
    const technician = techniciansForAssignment.value?.find(t => t.id === taskAssignmentData.value.technicianId);

    const data = await post('/api/technician-tasks/generate-request', {
      task: {
        description: taskAssignmentData.value.description,
        priority: taskAssignmentData.value.priority
      },
      ticket: selectedTicket.value,
      conversations: ticketConversations.value,
      aiSummary: aiSummary.value,
      technician: technician
    });

    if (data.success) {
      generatedTaskRequestForCreation.value = data.request;
    } else {
      throw new Error(data.error || 'Chyba pri generovani');
    }
  } catch (err) {
    taskRequestErrorForCreation.value = err.message;
    console.error('Error generating task request for creation:', err);
  } finally {
    generatingTaskRequestForCreation.value = false;
  }
}

async function saveTaskAssignment() {
  if (!selectedTicket.value || !taskAssignmentData.value.technicianId) return;

  savingTask.value = true;

  try {
    const taskData = await post('/api/technician-tasks/create', {
      ticketId: selectedTicket.value.id,
      ticketSubject: selectedTicket.value.subject,
      technicianId: taskAssignmentData.value.technicianId,
      description: taskAssignmentData.value.description,
      priority: taskAssignmentData.value.priority,
      aiSummary: aiSummary.value,
      aiGeneratedRequest: generatedTaskRequestForCreation.value,
      customerName: taskAssignmentData.value.customerName,
      customerEmail: taskAssignmentData.value.customerEmail,
      customerPhone: taskAssignmentData.value.customerPhone,
      customerAddress: taskAssignmentData.value.customerAddress
    });

    taskSaved.value = true;

    // Optionally send email
    const shouldSendEmail = confirm('Ukol vytvoren! Chcete technikovi odeslat email?');
    if (shouldSendEmail) {
      await sendTaskEmail(taskData.task.id);
    }

    setTimeout(() => {
      closeTaskAssignment();
    }, 1500);

  } catch (err) {
    console.error('Error saving task:', err);
    alert('Chyba pri ukladani ukolu: ' + err.message);
  } finally {
    savingTask.value = false;
  }
}

async function sendTaskEmail(taskId) {
  try {
    const data = await post(`/api/technician-tasks/send-email?id=${taskId}`, {});
    // Open Gmail compose with the generated email
    const email = data.email;
    const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email.to)}&su=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.text)}`;
    window.open(gmailUrl, '_blank');
  } catch (err) {
    console.error('Error sending task email:', err);
  }
}

async function openTicketDetail(ticket) {
  selectedTicket.value = ticket;
  ticketConversations.value = [];
  loadingConversations.value = true;
  aiSummary.value = null;
  aiMetrics.value = null;
  aiSummaryError.value = null;
  loadingAiSummary.value = false;
  showWhatsappFormat.value = false;
  whatsappCopied.value = false;
  savingNote.value = false;
  noteSaved.value = false;
  existingSummaryDate.value = null;
  hasNewCommunication.value = false;

  try {
    // Fetch conversations from Freshdesk
    const data = await get('/api/freshdesk/tickets', { ticketId: ticket.id });

    if (data.success) {
      const conversations = data.conversations || [];

      // Filter out old AI summary notes from Freshdesk (legacy)
      const realConversations = conversations.filter(c => !isAiSummaryNote(c));
      ticketConversations.value = realConversations;

      // Update ticket with fresh data
      if (data.ticket) {
        selectedTicket.value = {
          ...selectedTicket.value,
          description: data.ticket.descriptionHtml || data.ticket.description,
          requester_email: data.ticket.requesterEmail
        };
      }

      // Load AI summary from our database (not Freshdesk!)
      try {
        const summaryData = await get('/api/freshdesk/get-summary', { ticketId: ticket.id });
        if (summaryData.success && summaryData.found) {
          aiSummary.value = summaryData.summary;
          existingSummaryDate.value = summaryData.createdAt;
          noteSaved.value = true;

          // Check if there's new communication since the summary was created
          const savedConversationCount = summaryData.conversationCount || 0;
          const currentConversationCount = realConversations.length;
          hasNewCommunication.value = currentConversationCount > savedConversationCount;
        }
      } catch (summaryErr) {
        console.error('Failed to load summary:', summaryErr);
      }
    }
  } catch (err) {
    console.error('Failed to load conversations:', err);
  } finally {
    loadingConversations.value = false;
  }
}

function isAiSummaryNote(conv) {
  if (conv.type !== 'note') return false;
  const body = conv.bodyHtml || conv.body || '';
  return body.includes('AI Souhrn') || body.includes('🤖 AI Souhrn');
}


function formatDescription(html) {
  if (!html) return '<em>Bez popisu</em>';
  // Sanitize HTML using DOMPurify to prevent XSS
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'span', 'div', 'blockquote', 'pre', 'code'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false
  });
}

async function generateAiSummary() {
  if (!selectedTicket.value) return;

  loadingAiSummary.value = true;
  aiSummaryError.value = null;
  aiSummary.value = null;
  aiTokenUsage.value = null;
  aiMetrics.value = null;

  try {
    const data = await post('/api/freshdesk/summarize', {
      ticket: selectedTicket.value,
      conversations: ticketConversations.value
    });

    if (data.success) {
      aiSummary.value = data.summary;
      aiTokenUsage.value = data.usage || null;
      aiMetrics.value = data.metrics || null;
    } else {
      throw new Error(data.error || 'Chyba pri generovani souhrnu');
    }
  } catch (err) {
    aiSummaryError.value = err.message;
    console.error('AI summary error:', err);
  } finally {
    loadingAiSummary.value = false;
  }
}

async function saveNoteToFreshdesk() {
  // Note: This function now saves to our DB, not Freshdesk (to avoid updating ticket's updated_at)
  if (!selectedTicket.value || !aiSummary.value) return;

  savingNote.value = true;

  try {
    // Save to our database with current conversation count
    const currentConversationCount = ticketConversations.value.length;

    const data = await post('/api/freshdesk/save-note', {
      ticketId: selectedTicket.value.id,
      summary: aiSummary.value,
      conversationCount: currentConversationCount,
      // Metadata for fulltext search
      requesterName: selectedTicket.value.requester || null,
      requesterEmail: selectedTicket.value.requester_email || null,
      subject: selectedTicket.value.subject || null,
      status: selectedTicket.value.status || null,
      createdAt: selectedTicket.value.created || selectedTicket.value.created_at || null
    });

    if (data.success) {
      noteSaved.value = true;
      existingSummaryDate.value = data.createdAt;
      hasNewCommunication.value = false; // Just saved, no new communication
      // Mark ticket as having AI summary
      if (selectedTicket.value) {
        selectedTicket.value.hasAiSummary = true;
      }
    } else {
      throw new Error(data.error || 'Chyba pri ukladani');
    }
  } catch (err) {
    console.error('Save note error:', err);
    alert('Chyba pri ukladani poznamky: ' + err.message);
  } finally {
    savingNote.value = false;
  }
}

function formatConvDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatMinutes(minutes) {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

// Daily stats functions
async function toggleDailyReport() {
  showDailyReport.value = !showDailyReport.value;
  if (showDailyReport.value && !dailyStats.value) {
    await loadDailyStats();
  }
}


function formatDailyStatsDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const days = ['neděli', 'pondělí', 'úterý', 'středu', 'čtvrtek', 'pátek', 'sobotu'];
  const day = days[date.getDay()];
  return `${day} ${date.getDate()}.${date.getMonth() + 1}.`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('cs-CZ');
}

// Daily report functions
async function generateDailyReport() {
  generatingDailyReport.value = true;
  dailyReport.value = null;

  try {
    const date = dailyReportDate.value; // Always send actual date (ISO format)

    const data = await post('/api/freshdesk/daily-report/generate', { date });
    if (data.success) {
      dailyReport.value = data.report;
      dailyReportCached.value = data.cached || false;
      dailyReportChanges.value = null; // Reset changes when loading new report
    } else {
      console.error('Error generating daily report:', data.error);
    }
  } catch (err) {
    console.error('Error generating daily report:', err);
  } finally {
    generatingDailyReport.value = false;
  }
}

async function checkDailyReportChanges() {
  if (!dailyReport.value?.fingerprint) return;

  checkingDailyReportChanges.value = true;
  dailyReportChanges.value = null;

  try {
    // Get current date from report (always ISO format now)
    const date = dailyReport.value.period?.startDate || dailyReportDate.value;
    if (!date) return;

    const data = await get('/api/freshdesk/daily-report/check-changes', { date });
    if (data.success && data.hasFingerprint) {
      dailyReportChanges.value = {
        hasChanges: data.hasChanges,
        changes: data.changes,
        cached: data.cached
      };
    }
  } catch (err) {
    console.error('Error checking daily report changes:', err);
  } finally {
    checkingDailyReportChanges.value = false;
  }
}

async function loadSavedDailyReport(periodKey) {
  generatingDailyReport.value = true;
  dailyReport.value = null;
  showSavedDailyReportsDropdown.value = false;

  try {
    const data = await get('/api/freshdesk/daily-report/get', { periodKey });
    if (data.success) {
      dailyReport.value = data.report.data;
      dailyReportCached.value = true; // Always cached when loading saved
      dailyReportChanges.value = null; // Reset changes
    }
  } catch (err) {
    console.error('Error loading daily report:', err);
  } finally {
    generatingDailyReport.value = false;
  }
}

async function toggleSavedDailyReports() {
  showSavedDailyReportsDropdown.value = !showSavedDailyReportsDropdown.value;

  if (showSavedDailyReportsDropdown.value && savedDailyReports.value.length === 0) {
    loadingSavedDailyReports.value = true;
    try {
      const data = await get('/api/freshdesk/daily-report/list', { limit: 14 });
      if (data.success) {
        savedDailyReports.value = data.reports;
      }
    } catch (err) {
      console.error('Error loading saved daily reports:', err);
    } finally {
      loadingSavedDailyReports.value = false;
    }
  }
}

function formatDailyReportLabel(report) {
  if (!report.reportDate) return report.periodKey;
  const date = new Date(report.reportDate);
  const dayNames = ['Ne', 'Po', 'Ut', 'St', 'Ct', 'Pa', 'So'];
  const label = `${dayNames[date.getDay()]} ${date.getDate()}.${date.getMonth() + 1}.`;
  return report.isWeekend ? `${label} (vikend)` : label;
}

// Monthly report functions
async function generateMonthlyReport() {
  generatingMonthlyReport.value = true;
  monthlyReport.value = null;
  monthlyReportProgress.value = {
    status: 'starting',
    progress: 0,
    currentStep: 'Nacitam tickety...',
    processedTickets: 0,
    totalTickets: 0,
    tokens: { input: 0, output: 0 },
    estimatedCostUsd: 0
  };

  try {
    // Calculate period
    const { periodStart, periodEnd } = calculateReportPeriod(monthlyReportPeriod.value);

    // Start generation
    const data = await post('/api/freshdesk/monthly-report/generate', { periodStart, periodEnd });

    if (!data.success) {
      throw new Error(data.error || 'Failed to start report generation');
    }

    // If cached report exists
    if (data.cached && data.status === 'completed') {
      const reportData = await get('/api/freshdesk/monthly-report/get', { reportId: data.reportId });
      if (reportData.success && reportData.report?.reportData) {
        monthlyReport.value = reportData.report.reportData;
        generatingMonthlyReport.value = false;
        return;
      }
    }

    monthlyReportProgress.value.totalTickets = data.totalTickets || 0;

    // Start polling and batch processing
    const reportId = data.reportId;
    await processMonthlyReportBatches(reportId);

  } catch (err) {
    console.error('Error generating monthly report:', err);
    monthlyReportProgress.value.currentStep = `Chyba: ${err.message}`;
  } finally {
    generatingMonthlyReport.value = false;
  }
}

function printMonthlyReport() {
  if (!monthlyReport.value) return;

  const report = monthlyReport.value;
  const lifecycle = report.lifecycle || {};
  const closedInPeriod = lifecycle.closedInPeriod || {};
  const stillOpen = lifecycle.stillOpen || {};

  // Build HTML for print
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Mesicni Report - ${report.period?.label || ''}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; font-size: 12px; color: #333; }
    h1 { font-size: 18px; color: #7c3aed; margin-bottom: 5px; }
    h2 { font-size: 14px; color: #666; margin: 15px 0 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
    .header { margin-bottom: 20px; }
    .subtitle { color: #666; font-size: 12px; }
    .grid { display: grid; gap: 10px; margin-bottom: 15px; }
    .grid-2 { grid-template-columns: 1fr 1fr; }
    .grid-4 { grid-template-columns: 1fr 1fr 1fr 1fr; }
    .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .card-green { background: #f0fdf4; border-color: #bbf7d0; }
    .card-amber { background: #fffbeb; border-color: #fde68a; }
    .card-red { background: #fef2f2; border-color: #fecaca; }
    .stat { text-align: center; padding: 8px; }
    .stat-value { font-size: 20px; font-weight: bold; }
    .stat-label { font-size: 10px; color: #666; }
    .green { color: #16a34a; }
    .red { color: #dc2626; }
    .amber { color: #d97706; }
    .purple { color: #7c3aed; }
    .row { display: flex; justify-content: space-between; margin: 3px 0; }
    .indent { padding-left: 10px; border-left: 2px solid #ddd; margin: 5px 0; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; }
    th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
    th { background: #f9fafb; font-weight: 600; }
    .text-right { text-align: right; }
    .insights { background: #faf5ff; border: 1px solid #e9d5ff; border-radius: 8px; padding: 12px; }
    .insights h3 { color: #7c3aed; font-size: 12px; margin-bottom: 8px; }
    .insights ul { margin: 5px 0 10px 15px; }
    .insights li { margin: 3px 0; }
    .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #999; }
    @media print { body { padding: 10px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>HOTJET - Mesicni AI Report</h1>
    <div class="subtitle">${report.period?.label || ''} | Vygenerovano: ${new Date().toLocaleDateString('cs-CZ')}</div>
  </div>

  <h2>Prehled</h2>
  <div class="grid grid-4">
    <div class="stat"><div class="stat-value">${report.volume?.totalTickets || 0}</div><div class="stat-label">Celkem ticketu</div></div>
    <div class="stat"><div class="stat-value purple">${report.volume?.processedTickets || 0}</div><div class="stat-label">Zpracovano</div></div>
    <div class="stat"><div class="stat-value green">${report.sentiment?.positiveRate || 0}%</div><div class="stat-label">Pozitivni</div></div>
    <div class="stat"><div class="stat-value red">${report.sentiment?.negativeRate || 0}%</div><div class="stat-label">Negativni</div></div>
  </div>

  <h2>Zivotni cyklus ticketu</h2>
  <div class="grid grid-2">
    <div class="card card-green">
      <div class="row"><strong>Uzavreno v obdobi</strong><span class="green" style="font-size:16px;font-weight:bold">${closedInPeriod.total || 0}</span></div>
      <div class="row"><span>Nove vytvorene a uzavrene:</span><span>${closedInPeriod.createdInPeriod || 0}</span></div>
      <div class="row"><span>Backlog uzavreny:</span><span>${closedInPeriod.createdBefore?.total || 0}</span></div>
      ${closedInPeriod.createdBefore?.total > 0 ? `
      <div class="indent">
        <div class="row"><span>do 14 dni:</span><span>${closedInPeriod.createdBefore?.within14days || 0}</span></div>
        <div class="row"><span>14-30 dni:</span><span>${closedInPeriod.createdBefore?.within30days || 0}</span></div>
        <div class="row"><span>30-60 dni:</span><span>${closedInPeriod.createdBefore?.within60days || 0}</span></div>
        <div class="row"><span class="amber">90+ dni:</span><span class="amber">${closedInPeriod.createdBefore?.over90days || 0}</span></div>
      </div>` : ''}
      <div class="row" style="margin-top:8px;padding-top:8px;border-top:1px solid #bbf7d0"><span>Ø doba reseni:</span><span><strong>${closedInPeriod.avgResolutionDays || 0} dni</strong></span></div>
    </div>
    <div class="card card-amber">
      <div class="row"><strong>Stale otevreno</strong><span class="amber" style="font-size:16px;font-weight:bold">${stillOpen.total || 0}</span></div>
      <div class="row"><span>Nove (cekajici):</span><span>${stillOpen.createdInPeriod || 0}</span></div>
      <div class="row"><span>Backlog (stare):</span><span>${stillOpen.createdBefore?.total || 0}</span></div>
      ${stillOpen.createdBefore?.total > 0 ? `
      <div class="indent">
        <div class="row"><span>14+ dni:</span><span>${stillOpen.createdBefore?.over14days || 0}</span></div>
        <div class="row"><span>30+ dni:</span><span>${stillOpen.createdBefore?.over30days || 0}</span></div>
        <div class="row"><span class="amber">60+ dni:</span><span class="amber">${stillOpen.createdBefore?.over60days || 0}</span></div>
        <div class="row"><span class="red">90+ dni:</span><span class="red">${stillOpen.createdBefore?.over90days || 0}</span></div>
      </div>` : ''}
    </div>
  </div>
  <div class="grid grid-4" style="margin-top:10px">
    <div class="stat"><div class="stat-value">${lifecycle.newTickets || 0}</div><div class="stat-label">Novych</div></div>
    <div class="stat"><div class="stat-value green">${lifecycle.resolutionRate || 0}%</div><div class="stat-label">Vyreseno</div></div>
    <div class="stat"><div class="stat-value purple">${lifecycle.backlogCleared || 0}</div><div class="stat-label">Backlog</div></div>
    <div class="stat"><div class="stat-value ${lifecycle.trend > 0 ? 'red' : 'green'}">${lifecycle.trend > 0 ? '+' : ''}${lifecycle.trend || 0}</div><div class="stat-label">Trend</div></div>
  </div>

  <h2>Sentiment zakazniku</h2>
  <div class="grid" style="grid-template-columns: repeat(5, 1fr)">
    <div class="stat"><div class="stat-value green">${report.sentiment?.positive || 0}</div><div class="stat-label">Pozitivni</div></div>
    <div class="stat"><div class="stat-value">${report.sentiment?.neutral || 0}</div><div class="stat-label">Neutralni</div></div>
    <div class="stat"><div class="stat-value red">${report.sentiment?.negative || 0}</div><div class="stat-label">Negativni</div></div>
    <div class="stat"><div class="stat-value amber">${report.sentiment?.frustrated || 0}</div><div class="stat-label">Frustrovani</div></div>
    <div class="stat"><div class="stat-value purple">${report.sentiment?.urgent || 0}</div><div class="stat-label">Urgentni</div></div>
  </div>

  <h2>Vykonnost agentu</h2>
  <table>
    <thead><tr><th>Agent</th><th class="text-right">Ticketu</th><th class="text-right">+</th><th class="text-right">-</th></tr></thead>
    <tbody>
      ${(report.agents || []).map(a => `
        <tr>
          <td>${a.name}</td>
          <td class="text-right">${a.ticketsHandled}</td>
          <td class="text-right green">+${a.sentiment?.positive || 0}</td>
          <td class="text-right red">-${a.sentiment?.negative || 0}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  ${report.insights && (report.insights.topIssues?.length || report.insights.improvementAreas?.length) ? `
  <h2>AI Poznatky</h2>
  <div class="insights">
    ${report.insights.topIssues?.length ? `
      <h3>Nejcastejsi problemy:</h3>
      <ul>${report.insights.topIssues.map(i => `<li>${typeof i === 'object' ? `<strong>${i.count}x</strong> ${i.issue}${i.details ? ` - ${i.details}` : ''}` : i}</li>`).join('')}</ul>
    ` : ''}
    ${report.insights.solutionsUsed?.length ? `
      <h3>Pouzita reseni:</h3>
      <ul>${report.insights.solutionsUsed.map(s => `<li><strong>${s.count}x</strong> ${s.solution} (${s.effectiveness})</li>`).join('')}</ul>
    ` : ''}
    ${report.insights.frustrationAnalysis ? `
      <h3>Analyza frustrace (celkem ${report.insights.frustrationAnalysis.total || report.sentiment?.frustrated || '?'}):</h3>
      <ul>
        <li>Z poruchy: <strong>${report.insights.frustrationAnalysis.fromInitialProblem?.count || 0}</strong></li>
        <li>Z prubehu reseni: <strong>${report.insights.frustrationAnalysis.fromResolutionProcess?.count || 0}</strong></li>
        <li>Z komunikace: <strong>${report.insights.frustrationAnalysis.fromCommunication?.count || 0}</strong></li>
        <li>Ostatni: <strong>${report.insights.frustrationAnalysis.fromOther?.count || 0}</strong></li>
      </ul>
    ` : ''}
    ${report.insights.improvementAreas?.length ? `
      <h3>Oblasti ke zlepseni:</h3>
      <ul>${report.insights.improvementAreas.map(a => `<li>${a}</li>`).join('')}</ul>
    ` : ''}
    ${report.insights.positiveHighlights?.length ? `
      <h3>Pozitivni zjisteni:</h3>
      <ul>${report.insights.positiveHighlights.map(p => `<li>${p}</li>`).join('')}</ul>
    ` : ''}
  </div>
  ` : ''}

  <div class="footer">
    HOTJET AI Report | Tokeny: ${((report.tokens?.input || 0) + (report.tokens?.output || 0)).toLocaleString()} | Cena: $${(report.tokens?.estimatedCostUsd || 0).toFixed(3)}
  </div>
</body>
</html>`;

  // Open print window
  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}

// Yearly report functions
async function generateYearlyReport() {
  yearlyReportLoading.value = true;
  yearlyReport.value = null;

  try {
    const data = await post('/api/freshdesk/yearly-report/generate', { year: yearlyReportYear.value });

    if (data.success) {
      yearlyReport.value = data.reportData;
    } else {
      alert(data.error || 'Nepodařilo se vygenerovat roční report');
    }
  } catch (err) {
    console.error('Error generating yearly report:', err);
    alert('Chyba při generování ročního reportu: ' + err.message);
  } finally {
    yearlyReportLoading.value = false;
  }
}

// Load saved reports list
async function loadSavedReports() {
  loadingSavedReports.value = true;
  try {
    const data = await get('/api/freshdesk/monthly-report/list');
    if (data.success) {
      savedReports.value = data.reports.filter(r => r.status === 'completed');
    }
  } catch (err) {
    console.error('Error loading saved reports:', err);
  } finally {
    loadingSavedReports.value = false;
  }
}

// Load a specific saved monthly report
async function loadSavedMonthlyReport(reportId) {
  generatingMonthlyReport.value = true;
  monthlyReport.value = null;
  showSavedReportsDropdown.value = false;

  try {
    const data = await get('/api/freshdesk/monthly-report/get', { reportId });
    if (data.success && data.report) {
      monthlyReport.value = data.report.reportData;
    } else {
      alert(data.error || 'Nepodařilo se načíst report');
    }
  } catch (err) {
    console.error('Error loading saved report:', err);
    alert('Chyba při načítání reportu');
  } finally {
    generatingMonthlyReport.value = false;
  }
}

// Toggle saved reports dropdown
function toggleSavedReports() {
  showSavedReportsDropdown.value = !showSavedReportsDropdown.value;
  if (showSavedReportsDropdown.value && savedReports.value.length === 0) {
    loadSavedReports();
  }
}

// Load available years for yearly report
async function loadSavedYearlyReports() {
  loadingSavedYearlyReports.value = true;
  try {
    const data = await get('/api/freshdesk/yearly-report/list');
    if (data.success) {
      savedYearlyReports.value = data.years || [];
    } else {
      console.error('Failed to load yearly reports:', data.error);
    }
  } catch (err) {
    console.error('Error loading yearly reports:', err);
  } finally {
    loadingSavedYearlyReports.value = false;
  }
}

// Load yearly report for specific year
async function loadSavedYearlyReport(year) {
  showSavedYearlyReportsDropdown.value = false;
  yearlyReportYear.value = year;
  generateYearlyReport();
}

// Toggle yearly reports dropdown
function toggleSavedYearlyReports() {
  showSavedYearlyReportsDropdown.value = !showSavedYearlyReportsDropdown.value;
  if (showSavedYearlyReportsDropdown.value && savedYearlyReports.value.length === 0) {
    loadSavedYearlyReports();
  }
}

function printYearlyReport() {
  if (!yearlyReport.value) return;

  const report = yearlyReport.value;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Rocni Report - ${report.period?.label || ''}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; font-size: 12px; color: #333; }
    h1 { font-size: 20px; color: #16a34a; margin-bottom: 5px; }
    h2 { font-size: 14px; color: #666; margin: 15px 0 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
    .header { margin-bottom: 20px; }
    .subtitle { color: #666; font-size: 12px; }
    .grid { display: grid; gap: 10px; margin-bottom: 15px; }
    .grid-2 { grid-template-columns: 1fr 1fr; }
    .grid-4 { grid-template-columns: 1fr 1fr 1fr 1fr; }
    .grid-5 { grid-template-columns: 1fr 1fr 1fr 1fr 1fr; }
    .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .stat { text-align: center; padding: 8px; }
    .stat-value { font-size: 20px; font-weight: bold; }
    .stat-label { font-size: 10px; color: #666; }
    .green { color: #16a34a; }
    .red { color: #dc2626; }
    .amber { color: #d97706; }
    .blue { color: #2563eb; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; }
    th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
    th { background: #f9fafb; font-weight: 600; }
    .text-center { text-align: center; }
    .issues { display: flex; flex-wrap: wrap; gap: 5px; }
    .issue-tag { background: #fef2f2; color: #dc2626; padding: 2px 6px; border-radius: 4px; font-size: 11px; }
    .insights { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px; margin-top: 15px; }
    .insights h3 { color: #16a34a; font-size: 12px; margin-bottom: 8px; }
    .insights ul { margin: 5px 0 10px 15px; }
    .insights li { margin: 3px 0; }
    .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #999; }
    @media print { body { padding: 10px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>HOTJET - Rocni AI Report</h1>
    <div class="subtitle">${report.period?.label || ''} | Vygenerovano: ${new Date().toLocaleDateString('cs-CZ')}</div>
  </div>

  <h2>Souhrnny prehled</h2>
  <div class="grid grid-4">
    <div class="stat"><div class="stat-value">${report.volume?.totalTickets || 0}</div><div class="stat-label">Celkem ticketu</div></div>
    <div class="stat"><div class="stat-value green">${report.volume?.closedTickets || 0}</div><div class="stat-label">Uzavreno</div></div>
    <div class="stat"><div class="stat-value blue">${report.volume?.closureRate || 0}%</div><div class="stat-label">Uspesnost</div></div>
    <div class="stat"><div class="stat-value amber">${report.volume?.openTickets || 0}</div><div class="stat-label">Otevrenych</div></div>
  </div>

  <h2>Rocni sentiment</h2>
  <div class="grid grid-5">
    <div class="stat"><div class="stat-value green">${report.sentiment?.positive || 0}</div><div class="stat-label">Pozitivni</div></div>
    <div class="stat"><div class="stat-value">${report.sentiment?.neutral || 0}</div><div class="stat-label">Neutralni</div></div>
    <div class="stat"><div class="stat-value red">${report.sentiment?.negative || 0}</div><div class="stat-label">Negativni</div></div>
    <div class="stat"><div class="stat-value amber">${report.sentiment?.frustrated || 0}</div><div class="stat-label">Frustrovani</div></div>
    <div class="stat"><div class="stat-value">${report.sentiment?.urgent || 0}</div><div class="stat-label">Nalehave</div></div>
  </div>

  ${report.topIssues?.length ? `
  <h2>Nejcastejsi problemy</h2>
  <div class="issues">
    ${report.topIssues.slice(0, 12).map(i => `<span class="issue-tag">${i.pattern} (${i.count}x)</span>`).join('')}
  </div>
  ` : ''}

  ${report.agents?.length ? `
  <h2>Vykon agentu</h2>
  <table>
    <thead>
      <tr><th>Agent</th><th class="text-center">Tickety</th><th class="text-center">Uzavreno</th><th class="text-center">Uspesnost</th><th class="text-center">Prumer dni</th></tr>
    </thead>
    <tbody>
      ${report.agents.slice(0, 10).map(a => `
        <tr>
          <td>${a.name}</td>
          <td class="text-center">${a.ticketsHandled}</td>
          <td class="text-center green">${a.ticketsClosed}</td>
          <td class="text-center">${a.closureRate}%</td>
          <td class="text-center">${a.avgResolutionDays ?? '-'}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  ` : ''}

  ${report.customers ? `
  <h2>Zakaznici</h2>
  <div class="grid grid-2">
    <div class="card">
      <div class="stat-value blue">${report.customers.serviceTechnicians?.ticketCount || 0}</div>
      <div class="stat-label">Tickety od servisnich techniku (${report.customers.serviceTechnicians?.uniqueCount || 0} unikatnich)</div>
    </div>
    <div class="card">
      <div class="stat-value">${report.customers.endCustomers?.ticketCount || 0}</div>
      <div class="stat-label">Tickety od koncovych zakazniku (${report.customers.endCustomers?.uniqueCount || 0} unikatnich)</div>
    </div>
  </div>
  ` : ''}

  ${report.insights ? `
  <div class="insights">
    <h3>AI Rocni analyza</h3>
    ${report.insights.yearSummary ? `<p style="margin-bottom: 10px;">${report.insights.yearSummary}</p>` : ''}
    ${report.insights.trends?.length ? `
      <h4 style="font-size: 11px; margin: 10px 0 5px;">Trendy:</h4>
      <ul>${report.insights.trends.map(t => `<li>${t.direction === 'up' ? '↑' : t.direction === 'down' ? '↓' : '→'} ${t.trend}</li>`).join('')}</ul>
    ` : ''}
    ${report.insights.recommendations?.length ? `
      <h4 style="font-size: 11px; margin: 10px 0 5px;">Doporuceni:</h4>
      <ul>${report.insights.recommendations.map(r => `<li><strong>${r.area}:</strong> ${r.suggestion}</li>`).join('')}</ul>
    ` : ''}
    ${report.insights.positiveHighlights?.length ? `
      <h4 style="font-size: 11px; margin: 10px 0 5px;">Pozitiva:</h4>
      <ul>${report.insights.positiveHighlights.map(p => `<li>${p}</li>`).join('')}</ul>
    ` : ''}
  </div>
  ` : ''}

  <div class="footer">
    HOTJET Rocni AI Report | Tokeny: ${((report.tokens?.input || 0) + (report.tokens?.output || 0)).toLocaleString()} | Cena: $${(report.tokens?.estimatedCostUsd || 0).toFixed(3)}
  </div>
</body>
</html>`;

  // Open print window
  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}

function calculateReportPeriod(period) {
  const now = new Date();
  let periodStart, periodEnd;

  if (period === 'last7days') {
    // Last 7 days
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    periodStart = formatLocalDate(sevenDaysAgo);
    periodEnd = formatLocalDate(now);
  } else if (period === 'previous') {
    // Previous month
    const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastOfPrevMonth = new Date(firstOfThisMonth - 1);
    const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);

    periodStart = formatLocalDate(firstOfPrevMonth);
    periodEnd = formatLocalDate(lastOfPrevMonth);
  } else if (period.startsWith('month_')) {
    // Specific month: month_YYYY_M format
    const parts = period.split('_');
    const year = parseInt(parts[1]);
    const month = parseInt(parts[2]) - 1; // JS months are 0-indexed

    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0); // Last day of month

    periodStart = formatLocalDate(firstOfMonth);
    periodEnd = formatLocalDate(lastOfMonth);
  } else {
    // Current month to today
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    periodStart = formatLocalDate(firstOfMonth);
    periodEnd = formatLocalDate(now);
  }

  return { periodStart, periodEnd };
}

// Generate entire year - process all missing months
async function generateEntireYear() {
  generatingYearlyBatch.value = true;
  yearlyBatchProgress.value = {
    status: 'checking',
    currentMonthIndex: 0,
    totalMonths: 0,
    currentMonth: '',
    monthsProcessed: [],
    monthsToProcess: [],
    overallProgress: 0,
    currentMonthProgress: 0,
    currentTicketId: null,
    processedTickets: 0,
    totalTickets: 0,
    tokens: { input: 0, output: 0 },
    estimatedCostUsd: 0
  };

  try {
    // Get current year
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-indexed

    // Fetch existing reports for this year
    const listData = await get('/api/freshdesk/monthly-report/list', { year: currentYear });

    // Find which months are already processed
    const processedMonths = new Set();
    if (listData.success && listData.reports) {
      for (const report of listData.reports) {
        if (report.status === 'completed') {
          // Extract month from periodKey (e.g., "2025-03")
          const match = report.periodKey.match(/\d{4}-(\d{2})/);
          if (match) {
            processedMonths.add(parseInt(match[1]));
          }
        }
      }
    }

    // Calculate which months need processing (January to last month)
    const monthsToProcess = [];
    for (let m = 1; m <= currentMonth; m++) { // currentMonth is 0-indexed, so this goes up to previous month
      if (!processedMonths.has(m)) {
        monthsToProcess.push(m);
      }
    }

    if (monthsToProcess.length === 0) {
      yearlyBatchProgress.value.status = 'completed';
      yearlyBatchProgress.value.currentMonth = 'Vsechny mesice jsou zpracovany';
      generatingYearlyBatch.value = false;
      return;
    }

    yearlyBatchProgress.value.monthsToProcess = monthsToProcess;
    yearlyBatchProgress.value.totalMonths = monthsToProcess.length;
    yearlyBatchProgress.value.status = 'processing';

    // Process each month
    for (let i = 0; i < monthsToProcess.length; i++) {
      const month = monthsToProcess[i];
      const monthName = monthNames[month - 1];

      yearlyBatchProgress.value.currentMonthIndex = i + 1;
      yearlyBatchProgress.value.currentMonth = `${monthName} ${currentYear}`;
      yearlyBatchProgress.value.currentMonthProgress = 0;
      yearlyBatchProgress.value.overallProgress = Math.round((i / monthsToProcess.length) * 100);

      // Calculate period for this month
      const periodStart = `${currentYear}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(currentYear, month, 0).getDate();
      const periodEnd = `${currentYear}-${String(month).padStart(2, '0')}-${lastDay}`;

      // Start generation for this month
      const data = await post('/api/freshdesk/monthly-report/generate', { periodStart, periodEnd });

      if (!data.success) {
        console.error(`Failed to start report for ${monthName}:`, data.error);
        continue;
      }

      // If cached, skip to next month
      if (data.cached && data.status === 'completed') {
        yearlyBatchProgress.value.monthsProcessed.push(`${monthName} ${currentYear}`);
        continue;
      }

      yearlyBatchProgress.value.totalTickets = data.totalTickets || 0;
      yearlyBatchProgress.value.processedTickets = 0;

      // Process batches for this month
      const reportId = data.reportId;
      await processYearlyMonthBatches(reportId, monthName, currentYear);

      yearlyBatchProgress.value.monthsProcessed.push(`${monthName} ${currentYear}`);
    }

    yearlyBatchProgress.value.status = 'completed';
    yearlyBatchProgress.value.overallProgress = 100;
    yearlyBatchProgress.value.currentMonth = 'Hotovo!';

    // Reload saved reports list
    await loadSavedReports();

  } catch (err) {
    console.error('Error generating yearly batch:', err);
    yearlyBatchProgress.value.status = 'error';
    yearlyBatchProgress.value.currentMonth = `Chyba: ${err.message}`;
  } finally {
    generatingYearlyBatch.value = false;
  }
}

// Process batches for a single month (used by generateEntireYear)
async function processYearlyMonthBatches(reportId, monthName, year) {
  let batchIndex = 0;
  let maxBatches = 200;

  while (batchIndex < maxBatches) {
    const batchData = await post('/api/freshdesk/monthly-report/process-batch', { reportId, batchIndex });

    if (!batchData.success && batchData.status !== 'completed') {
      throw new Error(batchData.error || 'Batch processing failed');
    }

    // Update progress
    const monthProgress = Math.round((batchData.totalProcessed / batchData.totalTickets) * 100) || 0;
    yearlyBatchProgress.value.currentMonthProgress = monthProgress;
    yearlyBatchProgress.value.processedTickets = batchData.totalProcessed || 0;
    yearlyBatchProgress.value.totalTickets = batchData.totalTickets || 0;
    yearlyBatchProgress.value.currentTicketId = batchData.lastProcessedTicketId || null;

    // Accumulate tokens
    yearlyBatchProgress.value.tokens.input += (batchData.tokens?.input || 0) - (yearlyBatchProgress.value.tokens.input || 0);
    yearlyBatchProgress.value.tokens.output += (batchData.tokens?.output || 0) - (yearlyBatchProgress.value.tokens.output || 0);
    yearlyBatchProgress.value.estimatedCostUsd = batchData.estimatedCostUsd || 0;

    if (batchData.status === 'completed' || !batchData.continue) {
      break;
    }

    batchIndex = batchData.nextBatch;
    await new Promise(r => setTimeout(r, 500));
  }
}

async function processMonthlyReportBatches(reportId) {
  let batchIndex = 0;
  let maxBatches = 200; // Safety limit

  while (batchIndex < maxBatches) {
    // Process batch
    const batchData = await post('/api/freshdesk/monthly-report/process-batch', { reportId, batchIndex });

    if (!batchData.success && batchData.status !== 'completed') {
      throw new Error(batchData.error || 'Batch processing failed');
    }

    // Update progress
    monthlyReportProgress.value = {
      status: batchData.status,
      progress: Math.round((batchData.totalProcessed / batchData.totalTickets) * 100) || 0,
      currentStep: batchData.status === 'completed' ? 'Dokonceno!' : `Zpracovano ${batchData.totalProcessed}/${batchData.totalTickets}`,
      processedTickets: batchData.totalProcessed || 0,
      totalTickets: batchData.totalTickets || 0,
      tokens: batchData.tokens || { input: 0, output: 0 },
      estimatedCostUsd: batchData.estimatedCostUsd || 0
    };

    // Check if completed
    if (batchData.status === 'completed') {
      monthlyReport.value = batchData.reportData;
      break;
    }

    // Check if more batches
    if (!batchData.continue) {
      break;
    }

    batchIndex = batchData.nextBatch;

    // Small delay between batches
    await new Promise(r => setTimeout(r, 500));
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Better formatting for email HTML content */
:deep(.ticket-content) {
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

:deep(.ticket-content p) {
  margin-bottom: 0.75em;
}

:deep(.ticket-content br) {
  display: block;
  content: "";
  margin-top: 0.5em;
}

:deep(.ticket-content div) {
  margin-bottom: 0.5em;
}

:deep(.ticket-content a) {
  color: #2563eb;
  text-decoration: underline;
}

:deep(.ticket-content blockquote) {
  border-left: 3px solid #d1d5db;
  padding-left: 1em;
  margin: 0.5em 0;
  color: #6b7280;
}

:deep(.ticket-content table) {
  border-collapse: collapse;
  margin: 0.5em 0;
}

:deep(.ticket-content td),
:deep(.ticket-content th) {
  border: 1px solid #e5e7eb;
  padding: 0.25em 0.5em;
}

:deep(.ticket-content img) {
  max-width: 100%;
  height: auto;
}

:deep(.ticket-content hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1em 0;
}
</style>
